import React, { useState, useEffect, useRef } from "react";
import api, { apiErr, assetUrl } from "@/lib/api";
import { DateConfirmBanner, dateFlagMessage, useDateConfirm } from "@/components/DateConfirmBanner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Btn, InfoTip } from "@/components/common";
import { AddCategoryInline, ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL } from "@/components/AddCategoryInline";
import { DeleteDialog } from "@/components/DeleteDialog";
import { CATEGORY_TOOLTIPS, CUSTOM_CATEGORY_MAX_LENGTH, GROUP_DISCLAIMER_FOOTER, CURRENCY_FIELD_HELP, money, AOTC_COMPUTER_GATE_CATEGORIES, localISO, fmtDate, nameOf, UNKNOWN_ACCOUNT_TYPE, STATUS_META } from "@/lib/format";
// The eligible list is read through the gate, never off GROUP_CATEGORIES
// directly — that is what let a catch-all reach this screen. (ZEN-100)
import { irsCategoriesFor, isOffListCategory, offListCategoryHint, isReservedCategoryName, OTHER_MANUAL } from "@/lib/categoryGate";
import { useParties } from "@/lib/parties";
import { schedulePreviewRequest, schedulePreviewDates, answerFor, pricingDate, AOTC_REPEATING_NOTE } from "@/lib/schedulePreview";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { usePaymentSources, psLabel, psMeta, psById, PS_TYPE_META, psForPayor, psDefaultForPayor, fundLast4 } from "@/lib/payments";
import { toast } from "sonner";
import { Info, ArrowsClockwise, Plus, X, Paperclip, FilePdf, WarningCircle, Warning, Trash, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { CurrencySelect } from "@/components/CurrencySelect";
import { currencyName, curCode, isUSD, sameCurrency } from "@/lib/currencies";
import { useFxCoverage, fxCoverageFor } from "@/lib/fxAdvisory";
import { fxHeading, NO_RATE_YET, noAutomaticRateFor, CONVERSION_BOX_TIP, ESTIMATE_RATE_NOTE } from "@/lib/fxPanel";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { SchoolEnrollmentBox, useSchoolChoices, useFollowThePick, periodLabel, namedSchool } from "@/components/SchoolEnrollmentBox";
import { useDateErrors } from "@/lib/useDateErrors";
import { useAskAI } from "@/context/AskAIContext";
import { MoneyInput } from "@/lib/accountingFormat";
import { RECEIPT_ACCEPT, hasAcceptedExtension, sendExpenseDocument, takeBackFromInbox } from "@/lib/uploads";
import { removalFacts, sameFacts, freshRemovalFacts, latestOnly } from "@/lib/removalFacts";
import { runUploads, attachmentFailureMessage } from "@/lib/uploadOutcome";
import { PaidByPlanText } from "@/components/PaidByPlanLine";
import { DRAW_QUESTION, DRAW_YES_LABEL, DRAW_NO_LABEL, DRAW_QUESTION_UNANSWERED, RESERVE_ANSWER_LABEL, holdsReservation, rateIsFinal, reserveAnswerOffered, reserveFigures, reserveLine, RESERVE_WAITS_FOR_RATE, creditCovers, COMPUTER_UNCONFIRMED } from "@/lib/aotcThirdAnswer";

// A RATE YOU TYPED BELONGS TO THE CURRENCY AND THE DAY YOU TYPED IT FOR — and
// this is the ONE place that says so, because the first version said it only in
// the currency picker and the pre-merge review immediately found the date field
// and the payment-source picker doing the same thing unguarded. Any change to
// either input clears the hand-entered rate, wherever it comes from.
const withRateInputs = (p, patch) => {
  // COMPARED THE WAY THE SERVER COMPARES: the DAY and the currency, not the raw
  // strings. An older row can carry a stored timestamp like
  // "2026-07-22T00:00:00Z" while the date field emits "2026-07-22" — the same
  // day — and a raw comparison read that as a move, throwing away a settled
  // hand-entered rate and replacing a tax figure that was already correct.
  // (Caught by the pre-merge gate.)
  const day = (d) => (d || "").slice(0, 10);
  // `sameCurrency` is the product's one answer to that question (ZEN-658); this
  // file used to carry its own two-line copy of it.
  const movedCurrency = patch.original_currency !== undefined
    && !sameCurrency(patch.original_currency, p.original_currency);
  const movedDate = patch.transaction_date !== undefined
    && day(patch.transaction_date) !== day(p.transaction_date);
  if (!movedCurrency && !movedDate) return { ...p, ...patch };
  // The tick goes with the rate it confirmed. Leaving it set while the rate
  // is cleared would carry a confirmation onto whatever is typed next.
  return { ...p, ...patch, fx_override: false, fx_rate: "", rate_confirmed: false };
};

// ── THE WORDS A PERSON SEES WHEN A RATE OR AN AMOUNT IS REFUSED. (ZEN-247)
//
// Approved by the owner on 2026-09-01, including her correction: a refusal says
// "Correct the exchange rate to save this expense", never "check the rate before
// saving" — if the save is flatly refused, the sentence has to say what to do
// rather than imply it might still go through.
//
// EVERY RATE IS WRITTEN AS THE CLAIM IT MAKES, never as a bare number: "1
// Canadian Dollar = 4,000 US dollars", not "a rate of 4000". A lone figure is
// unreadable — the owner said so of the first draft, and she was right.

/** Decimal places actually typed, counted from the string so 10.50 reads as 2. */
const decimalsOf = (v) => {
  const s = String(v ?? "");
  const dot = s.indexOf(".");
  return dot === -1 ? 0 : s.length - dot - 1;
};

const rateAsWords = (code, r) =>
  `1 ${currencyName(code)} = ${Number(r).toLocaleString(undefined, { maximumFractionDigits: 6 })} US dollars`;

/** "about twice" / "about 1.4 times" / "about 5,546 times" — never a bare ratio. */
const timesOff = (factor) => {
  if (factor >= 1.75 && factor <= 2.25) return "about twice";
  if (factor < 10) return `about ${factor.toFixed(1)} times`;
  return `about ${Math.round(factor).toLocaleString()} times`;
};

const rateRefusedSentence = (v) =>
  `You have entered ${rateAsWords(v.currency, v.typed)}. The published rate on ` +
  `${fmtDate(v.day)} was ${rateAsWords(v.currency, v.published)}. At the rate you entered, ` +
  `this expense would be recorded as ${money(v.wouldBe, "USD")} instead of ${money(v.shouldBe, "USD")}. ` +
  `Correct the exchange rate to save this expense.`;

const rateConfirmSentence = (v) =>
  `You have entered ${rateAsWords(v.currency, v.typed)}. The published rate on ` +
  `${fmtDate(v.day)} was ${Number(v.published).toLocaleString(undefined, { maximumFractionDigits: 6 })} — ` +
  `yours is ${timesOff(v.factor)} that, which is what happens when a rate is entered upside down. ` +
  `This expense would be recorded as ${money(v.wouldBe, "USD")} instead of ${money(v.shouldBe, "USD")}. ` +
  `If ${Number(v.typed).toLocaleString(undefined, { maximumFractionDigits: 6 })} is what your bank ` +
  `actually charged you, tick to confirm and it will be saved with your confirmation on the record.`;

const empty = {
  student_id: "", transaction_date: localISO(), payee: "", description: "",
  // Category is never pre-selected — a pre-filled category gets saved unread and
  // silently miscategorizes the expense (owner ruling, 2026-08-09). The picker
  // opens on a placeholder and the owner must choose.
  category: "", expense_group: "post_secondary", original_currency: "USD", original_amount: "", eligible_529: true,
  // NO eligibility_tax_year IN THE FORM'S OWN STATE. It used to be seeded here
  // and then carried into every request by the {...f} spread — so this client
  // was indistinguishable from a stale one, and the signal that retires the
  // accept-and-ignore window could never reach zero. The derived value for
  // DISPLAY is set by the effect below, on a separate key. (ZEN-172)
  draw_from_529: true, reimbursed_by_ref: "none",
  school_enrollment_id: "", payor_type: "", payor_party_id: "", payment_source_id: null, envelope_id: null, notes: "",
  fx_override: false, fx_rate: "", rate_confirmed: false, coa_period_id: "",
  aotc_reserved: false, aotc_reserved_amount: null, aotc_scholarship_confirmed: false,
};

// SIT_LABELS used to live here — a second wording of the same six arrangements,
// differing from both the setup form and the tax document. One table now, in
// lib/format.js, pinned to the server's. (ZEN-635)

// The add entry in the picker, its words and its box are the shared ones every
// screen uses (components/AddCategoryInline.jsx). Owner instruction, 2
// September 2026: one way of adding a category, so a problem is fixed in one
// place. (ZEN-512)

// WHAT THE ONE-OFF BOX REFUSES. These names belong to the picker's own entries
// that OPEN something rather than choose something, so they are never categories
// and must never be stored. Both are readable and short enough to type, and
// copying a label off the screen is the obvious way to end up typing one:
// "Other — enter manually" is its own value and label, while the add entry's
// value is a hidden marker, so the LABEL has to be named here too — guarding
// only values let it through to the record. (Review rounds 2-4.)
//
// REFUSING AT THE DOOR IS THE WHOLE FIX, and nothing is added to what the picker
// OFFERS. An earlier attempt also widened the offered list so a stored action
// value would not be drawn twice; round 5 was right that this made things worse,
// not better. It changed how the receipt review screen displays a value stored
// before this change — dropping the marker that says the name is not from the
// list — and this ticket promised to leave that screen behaving exactly as it
// did. With the door shut, no new such value can exist, and one stored earlier is
// displayed the way that screen has displayed it since ZEN-282: as its own
// option, marked. That is prior art, not a regression, and it reads better than
// the alternative because the marker is what tells the two apart.
const NON_ELIGIBLE_RESERVED_NAMES = [ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL, OTHER_MANUAL];

export default function ExpenseDialog({ open, onClose, onSaved, expense, defaultStudentId }) {
  const qc = useQueryClient();
  const { openDrawer: openAI, closeDrawer: closeAI, open: aiOpen } = useAskAI();
  const [f, setF] = useState(empty);
  // The tax year, for what THIS SCREEN needs to know — the year totals, the
  // tax-credit room, the school-period picker, and the line the person reads.
  // Deliberately NOT part of the form's payload state: it used to live in `f`
  // and ride into every request through the spread, which made this client
  // look exactly like a stale one. (ZEN-172)
  const [derivedTaxYear, setDerivedTaxYear] = useState(null);
  const [fx, setFx] = useState(null);
  // THE PUBLISHED RATE, KEPT EVEN WHILE THE OWNER IS TYPING THEIR OWN. (ZEN-247)
  //
  // `fx` above is deliberately cleared the moment manual override is ticked —
  // it means "the rate this expense is being priced at", and under an override
  // that is not the published one. But judging a typed rate needs the published
  // one, so without a second copy the screen cannot say anything until the
  // server has already refused the save. This holds the comparison figure and
  // nothing prices from it.
  const [fxCompare, setFxCompare] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [psAddOpen, setPsAddOpen] = useState(false);
  const [fxUsdField, setFxUsdField] = useState("");
  const [recur, setRecur] = useState({ enabled: false, intervalMonths: 1, day: null, endType: "date", endDate: "", occurrences: 12 });
  const [customCatOpen, setCustomCatOpen] = useState(false);
  const [customCatName, setCustomCatName] = useState("");
  // ADDING A CATEGORY NO LONGER LEAVES THE EXPENSE. (ZEN-56)
  //
  // This used to open Settings in a second browser tab. The half-filled expense
  // stayed behind on the first one, the new category was not chosen when you
  // came back, and most people simply lost what they had typed. The panel these
  // two pieces of state open is part of THIS dialog, so nothing is unmounted and
  // nothing to restore: every field keeps its value because it never went away.
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addReimburserOpen, setAddReimburserOpen] = useState(false);
  const [newReimburserNick, setNewReimburserNick] = useState("");
  // A RETRY MUST NOT CREATE A SECOND EXPENSE. (ZEN-79)
  //
  // When a new expense is created and one of its staged files is then refused,
  // the refused file stays staged so it can be retried — which is only safe if
  // pressing Save again attaches to the SAME expense. Left in create mode the
  // retry posts another expense and the owner has quietly doubled a tax record
  // while trying to fix an upload. So the dialog remembers what it created and
  // behaves, from that moment on, as though it had been opened on it.
  const [createdExpense, setCreatedExpense] = useState(null);
  // The FRESHER of the two wins, and only for the same record.
  //
  // `expense || createdExpense` was wrong in both directions. On an existing
  // expense the stale prop always won, so removing a document left it on
  // screen. And on a NEW one the memory outlived its dialog: closing "Add
  // expense" and opening it again treated the previously created expense as
  // the one being edited, and Save overwrote it instead of creating another.
  // It is reset whenever the dialog opens (see the effect below).
  const effectiveExpense =
    (createdExpense && (!expense || createdExpense.id === expense.id))
      ? createdExpense
      : expense;
  const isEdit = !!effectiveExpense;
  // ZEN-334: the dollars already matched to 529 withdrawals. The AOTC reserve
  // works independently of the match (Maryse, 2 Nov 2026), but it can only take
  // the part the 529 has not paid; the server refuses more (ZEN-164). The cost
  // is frozen once matched (ZEN-461), so the stored figure is the one on screen.
  // A match in the older format (a withdrawal pointer with no list of matches)
  // counts the WHOLE cost, as the server does (expense_logic._tied_529_cents).
  // Reading its alloc_usd instead could read $0 and let the form re-fill a
  // stored reserve (outside review, round 6).
  const _matchList = isEdit ? (effectiveExpense.allocations || []) : [];
  const matched529Usd = !isEdit ? 0
    : _matchList.length
      ? _matchList.reduce((s, a) => s + (Number(a.amount_usd) || 0), 0)
      : (effectiveExpense.allocated_withdrawal_id ? Number(effectiveExpense.usd_amount) || 0 : 0);
  const unpaidAfterMatch = matched529Usd > 0
    && matched529Usd < (Number(effectiveExpense?.usd_amount) || 0) - 0.005;
  // The ONE thing that says "nobody has classified this yet". Never inferred
  // from the stored booleans — an unclassified expense is stored with
  // eligible_529 false, which is indistinguishable from a real "No". (ZEN-114)
  const isUnclassified = expense?.status === "draft";
  // WHO PAID DRIVES THE PAYMENT ACCOUNT — and these two remember enough to do
  // that without ever overwriting a person's own choice. (ZEN-53)
  //   psTouched  — the account in the box was picked by hand, so no automatic
  //                pre-selection may replace it.
  //   openPayor  — who the dialog was told had paid when it opened. Nothing is
  //                pre-selected or cleared until that answer actually moves,
  //                which is what keeps an existing expense on the account it
  //                was saved with.
  const psTouched = useRef(false);
  const openPayor = useRef("");
  //   openPaymentSource — the account the expense was ALREADY recorded
  //                against when this dialog opened, so a detour through
  //                "529 Direct" and back does not lose it.
  const openPaymentSource = useRef(null);
  //   clearedByPlan — what was in the account box when "529 Direct" emptied
  //                it, so changing your mind hands back what YOU had, not
  //                what the record happened to hold.
  const clearedByPlan = useRef(null);
  // WHICH BENEFICIARY THE FORM IS ON *NOW*, readable from inside a request that
  // started earlier. The first attempt at the stale-response guard compared the
  // student captured when the save began against `f.student_id` — but the
  // completion handler was itself captured at that same moment, so both sides
  // were the OLD beneficiary and the guard agreed with itself every time. Its
  // own test caught it. A ref is the only thing here that is current at the
  // moment a promise resolves. (Adversarial review, round 4.)
  const studentIdNow = useRef("");
  studentIdNow.current = f.student_id;
  // WHICH ATTEMPT AT ADDING A CATEGORY THIS IS.
  //
  // Three rounds of review found the same defect wearing three different
  // clothes, and I fixed each one on its own terms before seeing the shape:
  //   · round 4 — switch beneficiary while the save is in flight;
  //   · round 6 — cancel, then open a DIFFERENT expense for the same one;
  //   · round 7 — cancel, then simply reopen the panel.
  // Every one is a reply from an attempt the owner has already walked away from,
  // landing in whatever is on screen now: choosing its category, closing the
  // panel they had just opened, wiping what they had typed into it.
  //
  // So this counter no longer tracks the dialog, or the beneficiary, but the
  // ATTEMPT — bumped whenever the panel opens, closes or is cancelled, and
  // whenever the dialog itself reopens. A reply that does not carry the current
  // number is finished business. That covers all three, and the next variant
  // nobody has thought of yet.
  const categoryAttempt = useRef(0);
  // ...AND THE ANSWER THAT DECIDES WHICH LIST APPLIES AT ALL.
  //
  // Round 8 found the counter alone was still not enough: answer "yes, this IS
  // 529-eligible" while a save is pending and the reply still wrote its non-529
  // label onto an expense now classified as eligible — a wrong tax
  // classification, stored, because the save door accepts any non-blank
  // category.
  //
  // Rather than add a fifth trigger to a list of four, this is now derived from
  // what a chosen category actually DEPENDS on: whose list it comes from (the
  // beneficiary), which list applies (the 529 answer), and whether the owner is
  // still on the attempt that asked. Those three are the whole of it by
  // construction, not by enumerating the ways it can go wrong.
  const eligibilityNow = useRef(null);
  eligibilityNow.current = f.eligible_529;
  const { onDateValidation, hasDateErrors } = useDateErrors();

  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: banks = [] } = useQuery({ queryKey: ["accounts-cards"], queryFn: async () => (await api.get("/accounts-cards")).data });
  const { data: psData } = usePaymentSources(true);
  // The beneficiary's 529 plans. They live apart from bank accounts and cards,
  // which is exactly why "529 Direct" used to offer no 529 account at all — the
  // one right answer was not on the menu. (ZEN-53)
  const { data: funds529 = [] } = useQuery({
    queryKey: ["fund-accounts", f.student_id],
    queryFn: async () => (await api.get(`/fund-accounts?student_id=${f.student_id}&include_archived=false`)).data,
    enabled: open && !!f.student_id,
  });
  const sources = React.useMemo(() => psData?.sources || [], [psData]);
  const psTypes = psData?.types || Object.keys(PS_TYPE_META);
  const { data: parties } = useParties();

  // Fetch statutory caps for the selected student + tax year
  const { data: capData } = useQuery({
    queryKey: ["dash-student", f.student_id, derivedTaxYear],
    queryFn: async () => (await api.get(`/dashboard/student/${f.student_id}?tax_year=${derivedTaxYear}`)).data,
    enabled: open && !!f.student_id && !!derivedTaxYear,
  });
  const statutoryCaps = capData?.statutory_caps || [];
  const selectedStudent = students.find((s) => s.id === f.student_id);
  // THE SCHOOL LEVEL THIS EXPENSE USES, decided here once. A saved expense keeps
  // the level it was saved under until its child or school is changed on it
  // (Maryse, 25 Oct 2026, ZEN-372); otherwise it follows the chosen school.
  const levelFor = (form) => {
    if (expense?.id && expense.expense_group && form.student_id === expense.student_id
        && (form.school_enrollment_id || "") === (expense.school_enrollment_id || "")) return expense.expense_group;
    const live = ((students.find((s) => s.id === form.student_id)?.schools) || []).filter((s) => !s.archived);
    return (live.find((s) => s.id === form.school_enrollment_id) || live[0])?.expense_group || "post_secondary";
  };
  const studentGroup = levelFor(f);

  // Which year this cost counts toward, and what it could follow. (ZEN-331)
  const [yearMoveNote, setYearMoveNote] = useState("");
  const [yearMoveBusy, setYearMoveBusy] = useState(false);
  useEffect(() => {
    // A note typed for ONE cost must never become the recorded reason for
    // another — the box empties whenever the dialog opens or the record under
    // it changes. (ZEN-331, adversarial diff review round 3.)
    setYearMoveNote("");
  }, [open, expense?.id]);
  const { data: yearOpts, refetch: refetchYearOpts } = useQuery({
    queryKey: ["expense-tax-year", expense?.id],
    queryFn: async () => (await api.get(`/expenses/${expense.id}/tax-year`)).data,
    // Owner-tier on the server; a beneficiary session simply gets no section.
    enabled: open && !!expense?.id,
    retry: false,
  });
  const moveTaxYear = async (countToward, withdrawalDate, tieUsd) => {
    setYearMoveBusy(true);
    try {
      const body = {
        count_toward: countToward,
        ...(yearOpts?.pins || {}),
      };
      body.note = yearMoveNote.trim();
      if (countToward !== "paid-year") {
        // Pins, not options: the server refuses a move whose request does not
        // carry the withdrawal date and tie amount the owner was shown.
        body.observed_withdrawal_date = withdrawalDate || "";
        body.observed_tie_usd = tieUsd ?? 0;
      }
      const { data } = await api.post(`/expenses/${expense.id}/tax-year`, body);
      toast.success(countToward === "paid-year"
        ? `This cost now counts toward ${data.eligibility_tax_year}, the year it was paid.`
        : `This cost now counts toward ${data.eligibility_tax_year} on your year totals and tax documents.`);
      setYearMoveNote("");
      await refetchYearOpts();
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dash-student"] });
      onSaved?.();
    } catch (e2) {
      toast.error(apiErr(e2));
      // A 409 means the record moved under the page — re-read it.
      await refetchYearOpts();
    } finally {
      setYearMoveBusy(false);
    }
  };

  // AOTC reserve state for this student + tax year
  const { data: aotcData } = useQuery({
    queryKey: ["aotc-reserve", f.student_id, derivedTaxYear],
    queryFn: async () => (await api.get(`/dashboard/aotc-reserve/${f.student_id}?tax_year=${derivedTaxYear}`)).data,
    // `=== true`: an unclassified expense holds null here, and react-query
    // throws on a non-boolean (ZEN-114). Asked whenever the third answer could
    // apply, or a reservation is held (ZEN-833).
    enabled: open && !!f.student_id && !!derivedTaxYear && f.eligible_529 === true && studentGroup === "post_secondary"
      && (creditCovers({ group: studentGroup, category: f.category }) || holdsReservation(f)),
  });

  // AOTC derived state. The rest (room, rate, the third answer) is worked out
  // after the exchange rate, below.
  const isHigherEd = studentGroup === "post_secondary";
  const isAotcEligibleCat = creditCovers({ group: studentGroup, category: f.category });
  const isComputerCat = AOTC_COMPUTER_GATE_CATEGORIES.has(f.category);
  // An existing reservation whose expense has since changed is ZEN-245's
  // question (with ZEN-204); this window only never hides it (ZEN-200).
  // The computer course-of-study answer is screen-only (ZEN-165). A held
  // reservation was saved with it answered, so it opens ticked.
  // A held reservation opens ticked only when it was saved on a computer
  // category, where the box was answered to save it. Any other held reservation
  // opens unticked, so changing its category to a computer asks (outside review,
  // ZEN-833 round 2).
  const [computerConfirmed, setComputerConfirmed] = useState(false);
  const [reserveChosenNow, setReserveChosenNow] = useState(false);

  useEffect(() => {
    if (open) {
      categoryAttempt.current += 1;
      setComputerConfirmed(holdsReservation(expense) && AOTC_COMPUTER_GATE_CATEGORIES.has(expense?.category));
      setReserveChosenNow(false);
      setPsAddOpen(false);
      setAddCatOpen(false);
      setReceiptFiles([]);
      setFxUsdField("");
      setRecur({ enabled: false, intervalMonths: 1, day: null, endType: "date", endDate: "", occurrences: 12 });
      setRecurCapConfirm(false);
      recurCapRef.current = false;
      psTouched.current = false;
      clearedByPlan.current = null;
      if (expense) {
        const pt = expense.payor_type || (["student", "owner", "529"].includes(expense.paid_by) ? expense.paid_by : "owner");
        // AN EXPENSE NOBODY HAS CLASSIFIED SHOWS NO ANSWER — not even a plausible
        // one. It is STORED with eligible_529 false, because there is nowhere
        // else to put "nobody has said yet", so pre-selecting from that value
        // would put the word "No" in front of the owner as though somebody had
        // chosen it. `null` means unanswered, and only the status can tell us.
        // (ZEN-114)
        const unclassified = expense.status === "draft";
        setF({ ...empty, ...expense, payor_type: pt, payor_party_id: expense.payor_party_id || "",
               reimbursed_by_ref: expense.reimbursed_by_ref || "none", payment_source_id: expense.payment_source_id || null,
               eligible_529: unclassified ? null : expense.eligible_529,
               category: unclassified ? "" : expense.category,
               draw_from_529: unclassified ? null : (expense.draw_from_529 !== undefined ? expense.draw_from_529 : !!expense.eligible_529),
               original_amount: expense.original_amount != null && !isNaN(parseFloat(expense.original_amount)) ? parseFloat(expense.original_amount).toFixed(2) : expense.original_amount,
               fx_rate: expense.fx_rate || "" });
        // Pre-fill USD field when editing a manually-overridden expense
        if (expense.fx_override && expense.fx_rate && expense.original_amount) {
          setFxUsdField((parseFloat(expense.original_amount) * parseFloat(expense.fx_rate)).toFixed(2));
        }
        openPayor.current = pt; // an existing expense keeps the account it was saved with
        openPaymentSource.current = expense.payment_source_id || null;
      } else { openPayor.current = ""; openPaymentSource.current = null; setF({ ...empty, student_id: defaultStudentId || "" }); }
      setFx(expense ? { rate: expense.fx_rate, rate_date: expense.fx_rate_date, source: expense.fx_source } : null);
    }
  }, [open, expense, defaultStudentId]);

  // Sync expense_group from the selected student
  useEffect(() => {
    if (!open) return;
    if (f.expense_group !== studentGroup) {
      // Read the level from the form as it is NOW, not as this render saw it:
      // on opening, the expense is loaded in the same instant.
      setF((p) => {
        const grp = levelFor(p);
        if (p.expense_group === grp) return p;
        const irsCats = irsCategoriesFor(grp);
        // Keep a category that is still valid for the new group; otherwise clear it
        // back to the placeholder rather than guessing one.
        return { ...p, expense_group: grp, category: irsCats.includes(p.category) ? p.category : "" };
      });
    }
  }, [open, f.expense_group, studentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // The tax year follows the date. FULL STOP — there is no manual override any
  // more, because there is no field to override it with: the server derives it
  // from the payment date and ignores anything sent. This keeps the screen's own
  // reads (the year totals, the tax-credit room, the school-period picker) in
  // step with what the server will store. (ZEN-172)
  useEffect(() => {
    if (!open) return;
    // CLEARED OR BROKEN MEANS NO ANSWER, not "keep the last one". Leaving the
    // previous year standing would let the year totals, the tax-credit room and
    // the school-period picker keep computing from a date that is no longer
    // there — quietly, and against a year the server would never store.
    const year = /^\d{4}-\d{2}-\d{2}$/.test((f.transaction_date || "").slice(0, 10))
      ? parseInt(f.transaction_date.slice(0, 4)) : NaN;
    setDerivedTaxYear(year >= 2000 && year <= 2100 ? year : null);
  }, [open, f.transaction_date]);

  // THE SCHOOL AND THE COA PERIOD COME FROM THE SERVER'S ONE RULE. (ZEN-460 /
  // ZEN-58, Maryse 15 Oct 2026.) The app picks the school from the date paid;
  // the box offers only schools that could apply to that year; the period is
  // shown read-only, as the save would store it. While an edit keeps its own
  // date and beneficiary the stored school stays (Q4).
  const keepStoredSchool = isEdit && !!effectiveExpense
    && (f.transaction_date || "").slice(0, 10) === (effectiveExpense.transaction_date || "").slice(0, 10)
    && f.student_id === effectiveExpense.student_id;
  // A settled or matched expense keeps its stored school whatever is edited,
  // as the server does (Q5); the box is locked and the form never replaces it.
  const moneyTied = isEdit && !!effectiveExpense && (
    ["settled_privately", "settled_529"].includes(effectiveExpense.closed_reason)
    || (effectiveExpense.allocations || []).length > 0 || !!effectiveExpense.allocated_withdrawal_id);
  // ZEN-826: the settled banner's other frozen answers — the two 529 answers,
  // Category, Paid by, Reimbursed by — locked wherever the banner shows. The
  // server refuses them too; the banner is the explanation.
  const answersFrozen = moneyTied || (isEdit && (expense?.status === "reconciled"
    || ["settled_privately", "settled_529"].includes(expense?.closed_reason)));
  const schoolChosen = useRef(false);
  const keepSchool = keepStoredSchool || moneyTied;
  const { data: schoolChoices } = useSchoolChoices({
    studentId: f.student_id, date: f.transaction_date, category: f.category,
    schoolId: namedSchool(schoolChosen, keepSchool, f.school_enrollment_id), enabled: open,
  });
  const chooseSchool = useFollowThePick({
    data: schoolChoices, value: f.school_enrollment_id,
    setValue: (id) => setF((p) => ({ ...p, school_enrollment_id: id })),
    resetKey: `${open}|${f.student_id}|${effectiveExpense?.id || ""}`, keepStored: keepSchool, chosenRef: schoolChosen,
    stored: effectiveExpense?.school_enrollment_id,
  });


  // THE PAYMENT ACCOUNT FOLLOWS WHO PAID. (ZEN-53)
  //
  // What this replaced fired exactly once per dialog opening and picked the
  // single household-wide default, whoever it belonged to. So all four answers
  // to "paid by" produced the same list and the same account, and changing the
  // answer changed nothing.
  //
  // Nothing happens at all unless the answer to "who paid" has actually
  // MOVED. Past that, in this order:
  //
  //   1. Moved to 529 Direct — the plan paid, so no bank account or card was
  //      involved and the box is emptied. This one comes FIRST, ahead of the
  //      two protections below, and deliberately: those protect an account
  //      the person chose or an expense they already saved, and neither is a
  //      reason to keep a card attached to a payment the person has just said
  //      came straight out of the plan. Anything else leaves the form showing
  //      a card while claiming the plan paid, and Save would send it.
  //      (Adversarial review, round 1.)
  //   2. The expense already exists — it keeps the account it was saved with;
  //      an edit never re-selects one account over another.
  //   3. The account was picked BY HAND — never replaced by an automatic one.
  //
  // Only past all three does the payer's OWN default get pre-selected. A
  // person with no default of their own gets an empty box — never a fall back
  // to somebody else's account.
  useEffect(() => {
    if (!open) return;
    const payor = f.payor_type;
    if (!payor) return;
    // EMPTYING THE BOX DOES NOT NEED THE ACCOUNT LIST — and waiting for it was
    // a way for the wrong thing to be saved. On an expense that already exists
    // the account comes from the record, not from the list, so a save made
    // while the list was still loading sent a card on an expense marked as
    // paid by the plan. The rule is enforced on the server as well, which is
    // what makes it a rule rather than a habit of this screen.
    // (Adversarial review, round 5.)
    if (payor === "529") {
      // The plan paying and a card paying cannot both be true, so the box is
      // emptied — but what was in it is put aside rather than thrown away.
      if (f.payment_source_id) {
        clearedByPlan.current = { id: f.payment_source_id, touched: psTouched.current };
        psTouched.current = false;
        setF((p) => withRateInputs(p, { payment_source_id: null }));
      }
      return;
    }

    // LEAVING THE PLAN HANDS BACK WHAT WAS TAKEN.
    //
    // Whichever payer you land on, not just the one you started from: an
    // earlier version only restored on the way home, so 529 Direct and then
    // Student left the box empty and Save wiped an account off a record that
    // had carried one for months. And it hands back what YOU had in the box,
    // not merely what the record holds — pick a different account, look at
    // 529 Direct, come back, and the account you chose is still there rather
    // than being quietly swapped for the recorded one. It stays visible in
    // the list even under a payer who does not own it; the list always keeps
    // whatever is selected. (Adversarial review, rounds 2, 3 and 4.)
    //
    // WHAT COMES BACK IS ONLY WHAT WAS SOMEBODY'S TO LOSE. On an expense that
    // already exists, that is whatever the box held — the record must not be
    // emptied by a detour. On a NEW one it is only an account the person chose
    // BY HAND; an account the form itself had pre-selected is not theirs to
    // protect, and putting it back would leave "Other" starting on an account
    // when it is meant to start on nothing.
    if (!f.payment_source_id) {
      const stash = clearedByPlan.current;
      const back = stash
        ? ((isEdit || stash.touched) ? stash.id : null)
        : (isEdit ? openPaymentSource.current : null);
      // ...AND ONLY IF IT IS STILL AN ANSWER FOR THE PAYER YOU LANDED ON.
      //
      // Handing back an account the new payer cannot own is not kindness: the
      // server refuses that pairing, so the form would be holding a selection
      // it knows the save will reject. Beneficiary's card, then 529 Direct,
      // then Owner is the route that found it. An existing expense is the one
      // exception — its recorded account is preserved whatever the payer,
      // because the server judges a pairing only when it CHANGES.
      // (Adversarial review of ZEN-283, round 4.)
      const backStillFits = !!back && (isEdit || !sources.length
        || psForPayor(sources, payor, f.student_id, null).some((x) => x.id === back));
      if (back && backStillFits) {
        if (stash) psTouched.current = stash.touched;
        clearedByPlan.current = null;
        setF((p) => withRateInputs(p, { payment_source_id: back }));
        return;
      }
      if (back) psTouched.current = false;   // it was theirs; it is not an answer here
    }
    clearedByPlan.current = null;
    if (!sources.length) return;   // nothing to choose from yet
    // An expense that already exists keeps the account it was saved with; an
    // edit never re-selects one account over another.
    if (isEdit) return;
    if (payor === openPayor.current) return;

    // A HAND-PICKED ACCOUNT IS PROTECTED FROM BEING REPLACED — but not from
    // the person changing WHO PAID to somebody who cannot own it. (ZEN-283)
    //
    // The first reading of "re-selecting must not overwrite an account the
    // person chose by hand" kept such an account selected across a change of
    // payer. The merge gate's reviewer showed where that leads: the account is
    // not on the new payer's list at all, so what gets saved is an expense
    // recorded as paid by one person and funded from another person's card,
    // which the server now refuses outright. Keeping a selection the save will
    // reject is not protecting anybody's choice — it is holding on to it long
    // enough to fail at the end.
    //
    // So the protection is scoped to what it was always for: within one payer,
    // nothing automatic replaces a hand-picked account. Change who paid and
    // the account follows, because under this ticket's own rule that is what
    // "who paid decides which accounts you are shown" means.
    if (psTouched.current) {
      // "NOT SPECIFIED" IS A CHOICE TOO. An emptied box can never be found in
      // anybody's list, so testing it the same way turned a deliberate "no
      // account" into the payer's default a moment later. (Adversarial review
      // of this follow-up.)
      if (!f.payment_source_id) return;
      const stillTheirs = psForPayor(sources, payor, f.student_id, null)
        .some((x) => x.id === f.payment_source_id);
      if (stillTheirs) return;
      psTouched.current = false;
    }
    const def = psDefaultForPayor(sources, payor, f.student_id);
    setF((p) => withRateInputs(p, {
      payment_source_id: def ? def.id : null,
      original_currency: curCode(def?.default_currency) || p.original_currency,
    }));
    // f.payment_source_id is deliberately NOT a dependency: this effect writes
    // it, and listing it here would make the write re-trigger the effect.
  }, [open, isEdit, f.payor_type, f.student_id, sources]); // eslint-disable-line

  // A dialog remembers nothing from the last time it was opened. (ZEN-79)
  // And nothing still in flight for the last one may land on this one.
  const removal = useRef(latestOnly()).current;
  useEffect(() => { removal.cancel(); setRemovingDocId(null); setRemovingDoc(false); setCreatedExpense(null); }, [open, expense?.id, removal]);
  // Nor after the form has left the screen altogether.
  useEffect(() => () => removal.cancel(), [removal]);

  // THE SAME SAVE PRESSED TWICE RECORDS ONCE. One key per opening of the form,
  // sent with every press of Save in that opening, so the server can tell a
  // repeat from a genuine second expense. (ZEN-758; lib/saveOnce.js)
  const saveKey = useSaveKey(`${open}:${expense?.id || "new"}`);

  const set = (k, v) => {
    if (k === "original_currency" || k === "transaction_date") setFxUsdField("");
    setF((p) => withRateInputs(p, { [k]: v }));
  };
  // ONE attachment at a time, never "the" attachment. (ZEN-79)
  const [removingDocId, setRemovingDocId] = useState(null);
  const [removingDoc, setRemovingDoc] = useState(false);
  const attachments = effectiveExpense?.attachments || [];
  // Read from the same list the rows are drawn from.
  // One removal wording for both kinds of document (owner ruling 2026-10-02).
  // Each sentence shows only when it is true of this document.
  const shownFacts = removalFacts(attachments, removingDocId);
  const removingIsTheLastOne = !!shownFacts?.isTheLastOne;
  const removingIsShared = !!shownFacts?.shared;
  // Whether the file itself goes is the server's answer, not the screen's guess.
  const removingDeletesTheFile = !!shownFacts?.deletesTheFile;
  // The answers go stale while this form is open, so the box opens on a fresh
  // read. A failed read opens no box. Every answer is acted on only if it is
  // still the newest request for the expense it was asked about: a late or
  // out-of-order one is dropped before it can touch anything.
  const askToRemove = async (docId) => {
    if (removingDocId) return;
    const ticket = removal.next();
    const expenseId = effectiveExpense.id;
    try {
      const { expense: fresh, facts } = await freshRemovalFacts(api, expenseId, docId);
      if (!removal.isCurrent(ticket)) return;
      if (fresh?.id === expenseId) setCreatedExpense(fresh);
      if (facts) setRemovingDocId(docId);
    } catch (e) { if (removal.isCurrent(ticket)) toast.error(apiErr(e)); }
  };
  const removeDocument = async () => {
    const ticket = removal.next();
    const expenseId = effectiveExpense.id;
    const docId = removingDocId;
    const shown = shownFacts;
    setRemovingDoc(true);
    try {
      // Checked again before acting. If the box was closed meanwhile, nothing
      // is removed; if the answers changed, nothing is removed and it redraws.
      const { expense: fresh, facts } = await freshRemovalFacts(api, expenseId, docId);
      if (!removal.isCurrent(ticket)) return;
      if (!sameFacts(facts, shown)) {
        if (fresh?.id === expenseId) setCreatedExpense(fresh);
        if (!facts) setRemovingDocId(null);
        setRemovingDoc(false);
        return;
      }
      const { data } = await api.delete(`/expenses/${expenseId}/documents/${docId}`);
      toast.success("Document removed");
      onSaved?.();
      // The removal happened. Only the form that asked for it is redrawn.
      if (!removal.isCurrent(ticket)) return;
      setRemovingDocId(null); setRemovingDoc(false);
      if (data?.id === expenseId) setCreatedExpense(data);
    } catch (e) {
      if (!removal.isCurrent(ticket)) return;
      toast.error(apiErr(e)); setRemovingDoc(false);
    }
  };
  const student = students.find((s) => s.id === f.student_id);
  const studentNick = student?.nickname || student?.full_name?.split(" ")[0] || "Beneficiary";
  const schools = student?.schools || [];

  // sources visible in dropdown: the payer's own accounts, plus the currently
  // selected one (even if archived, deleted, or somebody else's — an account
  // the record points at is never hidden from the person editing it).
  const visibleSources = psForPayor(sources, f.payor_type, f.student_id, f.payment_source_id);
  const selectedPs = psById(sources, f.payment_source_id);
  const legacyHint = isEdit && !f.payment_source_id && (expense?.payment_method || expense?.bank_account_id);
  // Paid by the plan itself, and nothing is standing in the payment-account box.
  // An older record that DOES name an account keeps its picker, so editing it
  // never hides what was actually saved.
  const paidByPlan = f.payor_type === "529" && !f.payment_source_id;
  // THE PLAN IS NAMED ONLY WHILE IT IS STILL BEING CHOSEN.
  //
  // The expense does not store which plan paid — the plan is on the matching
  // withdrawal — so this line can only ever be read off today's list of live
  // plans. On a NEW expense that is exactly right: one live plan IS the
  // account about to be used. On an expense recorded months ago it is a
  // guess dressed as a fact — archive that plan, add another, and reopening
  // an old expense would name a plan that never paid for it. So an existing
  // expense gets the sentence without the name. (Adversarial review, round 1.)
  const activeFund529 = (!isEdit && funds529.length === 1) ? funds529[0] : null;
  const fund529Label = activeFund529
    ? (psLabel({ type: "529_plan", state: activeFund529.state, last4: fundLast4(activeFund529) },
        parties?.owner?.full_name || parties?.owner?.nickname || "", student?.full_name || "")
       || activeFund529.provider || "")
    : "";
  const noAccountsForPayor = !isEdit && !f.payment_source_id
    && (f.payor_type === "owner" || f.payor_type === "student") && visibleSources.length === 0;
  const legacyBank = banks.find((b) => b.id === expense?.bank_account_id);
  const psOwnerName = (s) => {
    if (s.owner_type === "student") return students.find(st => st.id === s.student_id)?.full_name || "";
    return parties?.owner?.full_name || parties?.owner?.nickname || "";
  };

  const onSelectPs = (v) => {
    if (v === "__add__") { setPsAddOpen(true); return; }
    // Chosen by hand — changing who paid must not quietly replace it. (ZEN-53)
    psTouched.current = true;
    set("payment_source_id", v || null);
    const s = psById(sources, v);
    if (s?.default_currency) set("original_currency", s.default_currency);
  };

  // live FX
  const [fxUnavailable, setFxUnavailable] = React.useState(false);
  // NO RATE YET, BUT ONE WILL COME (ZEN-555). Distinct from `fxUnavailable`,
  // which is "no source publishes this currency, so type one": here a source
  // does publish it and simply has not answered for that day, so the expense
  // saves with no US-dollar amount and fills itself in. Which of the two it is
  // comes from the server's own statement of what the converter covers — the
  // browser keeps no list of its own (the ZEN-108 mistake).
  const [fxWaiting, setFxWaiting] = React.useState(false);
  const { coverage: fxCoverage } = useFxCoverage();
  // The repeating preview is the server's own answer, never worked out here.
  const recurDay = recur.day || parseInt(f.transaction_date?.slice(8, 10) || "1");
  const recurAsk = schedulePreviewRequest(recur, f.transaction_date, recurDay);
  const recurAskKey = recurAsk ? JSON.stringify(recurAsk) : "";
  const [recurAnswer, setRecurAnswer] = useState(null);
  useEffect(() => {
    // Asked the way the exchange-rate preview is. A refused or unanswered
    // question shows no dates.
    setRecurAnswer(null);   // every new question starts from no answer
    if (!recurAskKey) return undefined;
    let current = true;
    api.post("/expenses/schedule-preview", JSON.parse(recurAskKey))
      .then(({ data }) => { if (current) setRecurAnswer({ askedFor: recurAskKey, data }); })
      .catch(() => {});
    return () => { current = false; };
  }, [recurAskKey]);
  // An answer is shown only for the exact fields it was asked about, so the
  // moment a field changes the old list is gone, before the new one arrives.
  const recurPreview = React.useMemo(
    () => schedulePreviewDates(answerFor(recurAnswer, recurAskKey)), [recurAnswer, recurAskKey]);
  // A repeating expense is priced for its FIRST PAYMENT DATE, the date the
  // first expense is saved under, so the dollar figure here is the one saved.
  // (ZEN-708, owner ruling 2026-10-13.)
  const priceDate = pricingDate(isEdit ? "" : recurAskKey, recurAnswer, f.transaction_date);

  // EVERY RATE LOOKUP IS TAGGED, AND A LATE ONE IS THROWN AWAY.
  //
  // These requests were neither cancelled nor matched to the inputs that asked
  // for them. Change the currency and then the date, and the FIRST reply can
  // land AFTER the second — overwriting the rate and clearing the loading flag
  // — so the screen shows, and Save accepts, a tax-credit reservation converted
  // at a rate belonging to a currency or a date the owner has already moved on
  // from. The server independently checks the reservation against the expense's
  // own US dollar cost, so a stale HIGH rate is refused there; a stale LOW one
  // is a valid partial reservation and is accepted, quietly setting aside less
  // credit than the family is entitled to. Nothing on the screen would say so.
  //
  // The counter is the whole fix: a reply is only allowed to write anything if
  // it is still the newest request outstanding. (Pre-merge adversarial review —
  // it found this in the save gate added one round earlier, which is a fair
  // reminder that a fix is a change like any other.)
  const fxRequestSeq = React.useRef(0);
  const fetchFx = React.useCallback(async () => {
    if (isUSD(f.original_currency) || f.fx_override || !priceDate) {
      fxRequestSeq.current += 1;   // ...and cancel anything already in flight
      // "NO SOURCE PUBLISHES THIS CURRENCY" SURVIVES THE OVERRIDE IT SWITCHED
      // ON. This used to clear it here — and switching Manual override on is
      // exactly what re-runs this lookup, so the amber line explaining WHY the
      // override had come on was wiped the instant it appeared and the family
      // was shown the ordinary "manual rate in use" note instead. It is cleared
      // when the currency or the day changes (below) or when the override is
      // unticked by hand. (ZEN-555, found by opening the real form in a test.)
      setFx(null); setFxWaiting(false); setFxLoading(false);
      if (!f.fx_override) setFxUnavailable(false);
      return;
    }
    const mine = ++fxRequestSeq.current;
    setFxLoading(true);
    setFxUnavailable(false);
    setFxWaiting(false);
    // THE LAST ANSWER WAS FOR THE LAST QUESTION. Kept while the new lookup
    // runs, a Canadian-dollar rate went on being shown, and multiplied, as the
    // rate of whatever currency or day had just been chosen — for as long as
    // a slow source took. (ZEN-555, review round 7.)
    setFx(null);
    try {
      const { data } = await api.post("/fx/preview", { date: priceDate, base: f.original_currency, target: "USD", source: "ecb" });
      if (mine !== fxRequestSeq.current) return;   // a newer request has superseded this one
      setFx(data);
    } catch (e) {
      if (mine !== fxRequestSeq.current) return;
      setFx(null);
      // WAIT FOR A RATE, OR ASK FOR ONE. Only a currency NO source publishes
      // switches Manual override on by itself. One a source does publish — or
      // one we cannot tell about, because the coverage answer has not arrived —
      // is left alone: the expense can be saved as it is and the rate fills
      // itself in. The server makes the same decision again at save, so a
      // wrong guess here cannot store anything it would not.
      if (fxCoverageFor(fxCoverage, f.original_currency).state === "manual") {
        setFxUnavailable(true);
        set("fx_override", true);
      } else {
        setFxWaiting(true);
      }
    }
    finally {
      // ONLY THE NEWEST REQUEST MAY CLEAR THE LOADING FLAG. A late reply doing
      // it re-opens the save gate while the current lookup is still running,
      // which is precisely the window this change exists to close.
      if (mine === fxRequestSeq.current) setFxLoading(false);
    }
  }, [f.original_currency, priceDate, f.fx_override, settings, fxCoverage]); // eslint-disable-line

  // A different CURRENCY is a different question: forget the last answer.
  // Declared BEFORE the lookup below so that it runs first.
  //
  // NOT A DIFFERENT DAY. "No source publishes this currency" is a fact about
  // the currency, true on every day. Clearing it on a day change wiped the
  // amber explanation whenever the pricing day moved while Manual override
  // stayed on — which is what happens when a new expense is switched to
  // Repeating: the lookup above then returns early and never puts it back.
  // A hand change to the transaction date still asks again, because that
  // switches the override off first. (ZEN-555, review round 4.)
  useEffect(() => { setFxUnavailable(false); }, [f.original_currency]);
  useEffect(() => { if (open) fetchFx(); }, [open, f.original_currency, priceDate, f.fx_override, fetchFx]);

  // ── The comparison rate. Same lookup, same race guard, different question.
  //
  // Its own request counter rather than sharing `fxRequestSeq`: the two run on
  // different triggers (this one does NOT re-run when the override is toggled,
  // because the published rate for a currency and a day does not depend on
  // whether somebody is overriding it), and one counter shared between two
  // request streams would let each cancel the other's replies.
  const fxCompareSeq = React.useRef(0);
  useEffect(() => {
    if (!open) return;
    if (isUSD(f.original_currency) || !priceDate) {
      fxCompareSeq.current += 1; setFxCompare(null); return;
    }
    const mine = ++fxCompareSeq.current;
    setFxCompare(null);   // same reason as the lookup above (review round 7): never judge a typed rate against the last currency's
    (async () => {
      try {
        const { data } = await api.post("/fx/preview", { date: priceDate, base: f.original_currency, target: "USD", source: "ecb" });
        if (mine !== fxCompareSeq.current) return;
        setFxCompare(data && data.rate > 0 ? data : null);
      } catch {
        // No published rate for this currency and day. There is then nothing to
        // compare a typed rate against and the screen says nothing — the same
        // hole the server has, named in rate_plausibility.py, not a silent one.
        if (mine === fxCompareSeq.current) setFxCompare(null);
      }
    })();
  }, [open, f.original_currency, priceDate]);

  // WHICH RATE THIS BOX IS ABOUT — the heading, the rate line and the dollar
  // figure, all three from ONE answer. For a saved expense whose currency and
  // day are untouched it is the rate STORED on the expense: that is the one the
  // server keeps on save, and today's preview for the same day can differ (an
  // expense deliberately left on an earlier day's rate would otherwise be
  // described, and re-multiplied, as if it held today's). Once either is
  // changed, or for a new expense, it is the server's preview.
  //
  // ONE ANSWER, because the first version of this gave the heading the stored
  // rate and left the line and the figure beneath it on the preview — a box
  // contradicting itself, and showing "—" for an expense that has a figure
  // whenever the preview happened to fail. (ZEN-555, review round 1.)
  const storedRateStands = !!expense && !f.fx_override && expense.fx_rate
    && expense.fx_source !== "manual"
    && sameCurrency(expense.original_currency, f.original_currency)
    && String(expense.transaction_date || "").slice(0, 10) === String(priceDate || "").slice(0, 10);
  const boxFx = storedRateStands
    ? { rate: expense.fx_rate, rate_date: expense.fx_rate_date, source: expense.fx_source, final: !expense.is_fx_estimate }
    : fx;
  const boxWaiting = fxWaiting && !f.fx_override && !storedRateStands;
  // "ENTERED BY HAND" ONLY ONCE A RATE HAS BEEN ENTERED. Manual override also
  // switches itself on when no source publishes the currency, with the rate box
  // still empty — and a heading claiming a rate that does not exist is "locked"
  // would contradict the amber line beneath it asking for one. (Review round 2.)
  const handRate = !!f.fx_override && (parseFloat(f.fx_rate) || 0) > 0;
  const fxHeadingText = isUSD(f.original_currency) ? "" : fxHeading({
    fx: f.fx_override ? null : boxFx, payDay: priceDate, waiting: boxWaiting, manual: handRate,
  });
  // THE ESTIMATE LABEL FOLLOWS THE RATE ON SHOW, not the expense as it was
  // opened: change an estimated expense's day to one whose rate is final and
  // the label must go, exactly as the heading changes. (Review round 2.)
  const boxIsEstimate = !f.fx_override && !isUSD(f.original_currency) && !!boxFx && boxFx.final === false;

  const amt = parseFloat(f.original_amount) || 0;
  const rate = f.fx_override ? (parseFloat(f.fx_rate) || 0) : (isUSD(f.original_currency) ? 1 : boxFx?.rate || 0);
  const usd = +(amt * rate).toFixed(2);

  // ── THE THIRD ANSWER (ZEN-833). Room is the year's target less what OTHER
  // expenses hold: this expense's stored figure is taken back out only when it
  // is in the same year's total (pre-merge reviews, ZEN-200).
  const storedHeld = isEdit && holdsReservation(effectiveExpense);
  const storedAmount = storedHeld ? (Number(effectiveExpense?.aotc_reserved_amount) || 0) : 0;
  const alreadyCounted = storedHeld && effectiveExpense?.eligibility_tax_year === derivedTaxYear ? storedAmount : 0;
  const aotcTarget = aotcData?.target || 0;
  const aotcRoom = Math.max(0, aotcTarget - Math.max(0, (aotcData?.marked || 0) - alreadyCounted));
  const reserveRateFinal = rateIsFinal({ currency: f.original_currency, handRate, fx: boxFx });
  // "Held": what this window holds now, or a stored reservation the owner has
  // just answered Yes or No to, but only while the card says Yes and the cost is
  // one the credit covers. Re-picking it then restores the stored figure (capped
  // above). Without that condition, re-picking after a category change reserved
  // a cost the credit does not cover (outside review, ZEN-833 round 3).
  const reserveHeld = holdsReservation(f) || (storedHeld && aotcData?.aotc_eligibility === "yes"
    && isHigherEd && isAotcEligibleCat);
  // ZEN-334 (Maryse, 2 Nov 2026, "Yes, independent"): the reservation works
  // independently of the 529 match, but can only take the part the 529 has not
  // paid; the server refuses more (ZEN-164). matched529Usd counts an older-format
  // match as the whole cost, as the server does.
  const reserveRoom = matched529Usd > 0 ? Math.min(aotcRoom, Math.max(0, usd - matched529Usd)) : aotcRoom;
  // Money tied freezes the use-529 answer (ZEN-826), not the reservation. So on a
  // frozen expense the answer that would flip the 529 is greyed, and "Partly" is
  // offered only where it keeps the 529 answer as stored (a drawn expense).
  const frozenDraw = answersFrozen ? (effectiveExpense?.draw_from_529 !== false) : null;
  const reserveOffered = f.eligible_529 === true && frozenDraw !== false && reserveAnswerOffered({
    held: reserveHeld, cardSaysYes: aotcData?.aotc_eligibility === "yes",
    qualifies: isHigherEd && isAotcEligibleCat, room: reserveRoom, rateFinal: reserveRateFinal,
  });
  // Would be offered but for the rate: the proposed sentence says why.
  // Same conditions as the offer itself, so it never promises an answer the
  // final rate would not bring (round 3).
  const reserveWaitsForRate = !reserveHeld && f.eligible_529 === true && frozenDraw !== false && !fxLoading && !reserveRateFinal
    && reserveAnswerOffered({ held: false, cardSaysYes: aotcData?.aotc_eligibility === "yes",
      qualifies: isHigherEd && isAotcEligibleCat, room: reserveRoom, rateFinal: true });
  // A stored set-aside may stay or shrink, never grow, unless the owner picks
  // the third answer again (her principle, ZEN-834, 31 Oct 2026: the app shrinks
  // a figure on its own only to stop double counting).
  const reserveCap = (!reserveRateFinal || (storedHeld && !reserveChosenNow)) ? storedAmount : Infinity;
  // ZEN-334: once matched, a stored reservation is kept exactly as it is, $0
  // included, until "Partly" is picked again in this window. Refitting it to the
  // year's room would move the 529's share, and with it the status, on an edit
  // that never touched the reservation.
  const keepStored = matched529Usd > 0 && storedHeld && !reserveChosenNow;
  const reserve = keepStored
    ? { reserved: storedAmount, rest: Math.max(0, Math.round((usd - storedAmount) * 100) / 100) }
    : reserveFigures(usd, reserveRoom, { cap: reserveCap });

  // The figure and the draw answer follow the cost and the room. Draw is Yes
  // only when the cost is above the reservation: a fully reserved expense
  // drawn from the 529 is refused by the server (ZEN-164).
  useEffect(() => {
    if (!holdsReservation(f) || !aotcData) return;
    if (reserve.reserved !== f.aotc_reserved_amount) set("aotc_reserved_amount", reserve.reserved);
    const draw = reserve.rest > 0.005;
    if (!answersFrozen && draw !== f.draw_from_529) set("draw_from_529", draw);
  }, [f.aotc_reserved, reserve.reserved, reserve.rest, aotcData, answersFrozen]); // eslint-disable-line

  const chooseDrawAnswer = (v) => {
    if (v === "") return;
    if (v === "reserve") { setReserveChosenNow(true); set("aotc_reserved", true); return; }
    set("aotc_reserved", false);
    set("aotc_reserved_amount", null);
    set("aotc_scholarship_confirmed", false);
    set("draw_from_529", v === "yes");
  };
  const drawAnswer = holdsReservation(f) ? "reserve"
    : (f.draw_from_529 === null || f.draw_from_529 === undefined ? "" : (f.draw_from_529 ? "yes" : "no"));

  // ── IS THE TYPED RATE BELIEVABLE? (ZEN-247)
  //
  // THE SERVER IS THE RULE; THIS IS THE COURTESY. `expense_logic.compute_usd`
  // refuses these on every write, including from a screen that never ran this
  // code. What this adds is telling the owner BEFORE they press Save, and
  // giving them the tick to answer with — a refusal that only arrives after a
  // failed save is a worse version of the same rule.
  //
  // The two numbers are the owner's ruling of 2026-09-01 and are the SAME two
  // in backend/rate_plausibility.py. They are duplicated here, which is a real
  // cost and is the thing to watch: a static test asserts these literals match
  // the backend's, so the pair cannot drift the way the AOTC category list did
  // (ZEN-166). Distance is measured as a ratio for the reason written out in
  // that file — a rate is multiplicative, so "twice" and "half" are equally
  // wrong and a percentage cannot say that.
  const RATE_CONFIRM_BEYOND = 1.25;
  const RATE_REFUSE_BEYOND = 10.0;
  const rateVerdict = React.useMemo(() => {
    if (!f.fx_override || isUSD(f.original_currency)) return null;
    const typed = parseFloat(f.fx_rate);
    const pub = fxCompare?.rate;
    if (!(typed > 0) || !(pub > 0)) return null;
    // ── ONLY A RATE SOMEBODY IS ENTERING IS JUDGED, exactly as the server
    // judges it. (Arena round 1.)
    //
    // Without this, opening an expense filed BEFORE these rules existed — one
    // carrying a rate more than 10x off — showed a refusal immediately and Save
    // rejected it, so correcting the payee on that record became impossible.
    // The server deliberately exempts an untouched rate for precisely that
    // reason, and the screen was overriding the exemption from the outside:
    // the fix bricking the recovery, avoided on the server and reintroduced
    // here. The same defect on both sides of one rule is what happens when the
    // second copy is written from the intent rather than from the first copy.
    //
    // Unchanged means unchanged in EVERY input the price is made from, not the
    // rate alone — editing the amount on a bad-rate row writes a brand-new
    // dollar figure from that bad rate, which is the hole the server had too.
    // ── "FILED" MEANS FILED, NOT MERELY SAVED — the same correction the server
    // needed. (Arena round 4.)
    //
    // The exemption below exists so a record the family has already filed is not
    // frozen by a rule introduced later. A row in the waiting room has been
    // filed by nobody, and the server judges it; treating it as filed here made
    // the screen quietly disagree with the server — hiding the warning on an
    // unchanged draft whose rate the server would refuse, so the owner met the
    // refusal only after pressing Save.
    const filed = isEdit && expense && expense.status !== "draft" ? expense : null;
    if (filed && filed.fx_override && filed.fx_rate != null) {
      const sameRate = parseFloat(filed.fx_rate) === typed;
      const sameAmount = parseFloat(filed.original_amount) === amt;
      // The shared answer, not a byte comparison: the server canonicalises both
      // sides, so a filed row holding `cad` re-picked as `CAD` is UNCHANGED
      // there while this once called it a move and demanded the rate be
      // confirmed again. Renamed because it now shadows nothing. (ZEN-658)
      const currencyUnchanged = sameCurrency(filed.original_currency, f.original_currency);
      const sameDay = String(filed.transaction_date || "").slice(0, 10)
        === String(f.transaction_date || "").slice(0, 10);
      // ── BUT A ROW THAT HAS NEVER BEEN CONFIRMED STILL NEEDS THE TICK SHOWN.
      // (Arena round 3.)
      //
      // The server accepts a first confirmation on an otherwise untouched row —
      // that is the whole escape route for a legacy odd rate, and splitting
      // such an expense tells the owner to use it. Suppressing the panel here
      // meant the tick never rendered, so the product asked for a confirmation
      // it gave no way to give. The only remaining escape was to change a rate
      // that was already correct. A screen that hides the control its own
      // message asks for is worse than one that never asked.
      const neverConfirmed = !filed.fx_rate_confirmed;
      const unchanged = sameRate && sameAmount && currencyUnchanged && sameDay;
      if (unchanged && !neverConfirmed) return null;
      if (unchanged && neverConfirmed) {
        const f0 = Math.max(typed / pub, pub / typed);
        // Only the confirmable band reopens. A filed rate beyond the outer line
        // has no tick to offer, and showing a refusal on a record the server
        // will happily accept unchanged is the freezing bug this guard exists
        // to avoid.
        if (f0 <= RATE_CONFIRM_BEYOND || f0 > RATE_REFUSE_BEYOND) return null;
      }
    }
    const factor = Math.max(typed / pub, pub / typed);
    if (factor <= RATE_CONFIRM_BEYOND) return null;
    return {
      refuse: factor > RATE_REFUSE_BEYOND,
      factor, typed, published: pub,
      currency: curCode(f.original_currency),
      wouldBe: +(amt * typed).toFixed(2),
      shouldBe: +(amt * pub).toFixed(2),
      day: fxCompare?.rate_date || priceDate,
    };
  }, [f.fx_override, f.fx_rate, f.original_currency, f.transaction_date, priceDate, fxCompare, amt, isEdit, expense]);

  // Ticking, then editing the rate again, must not carry the tick over to a
  // number nobody looked at. The server independently records WHICH rate was
  // confirmed and drops the confirmation if the row ends up with a different
  // one, so this is the screen agreeing with the server rather than the only
  // thing holding the line. The same when the day the rate is judged against
  // changes: a tick given against one day's published rate says nothing about
  // another day's. (ZEN-708)
  useEffect(() => { set("rate_confirmed", false); }, [f.fx_rate, priceDate]); // eslint-disable-line

  const recurOverflow = recurPreview.overflow === true;
  const recurLabel = {1:"Monthly",3:"Quarterly",4:"Semesterly",6:"Semi-annually",12:"Annually"}[recur.intervalMonths] || "";
  // A schedule may SPAN tax years — nothing is capped or dropped any more.
  //
  // This used to filter the preview down to the tax year on the form and tell
  // the owner the rest "will not be created", which matched a backend that
  // really did discard them (ZEN-112). Now that the backend keeps every
  // occurrence and files each under the year it is paid, that copy would be a
  // straight lie: the form promised 4 and the server would create 12.
  //
  // The confirmation is kept but repurposed. Crossing a tax-year boundary is
  // worth one deliberate look — it is the thing an owner most needs to know for
  // their records — so it now confirms the TRUE full count and names the years.
  const ty = parseInt(derivedTaxYear);
  const recurYears = [...new Set(recurPreview.map(d => d.getFullYear()))].sort();
  const recurSpansYears = recurYears.length > 1;
  // Mirror the server's year ceiling in the preview. The backend refuses a
  // schedule whose generated dates run past next year — correctly, since that is
  // the typo guard — but the form happily previewed "will create 24 expenses"
  // and only failed on save. Telling the owner up front which occurrences are
  // out of range beats a rejection after they commit. (ZEN-109)
  // UTC, matching the server and dateValidation.js. Reading the year in the
  // browser's own zone made this preview disagree with both of them for the
  // hours around New Year — warning that a valid schedule would be refused, or
  // staying silent about one the server was about to reject. Three places
  // enforcing one ceiling have to read one clock. (Pre-merge review, round 8.)
  const recurCeilingYear = new Date().getUTCFullYear() + 1;
  const recurBeyondCeiling = recurPreview.filter(d => d.getFullYear() > recurCeilingYear);
  const recurCapRef = React.useRef(false);
  const [recurCapConfirm, setRecurCapConfirm] = useState(false);

  // ZEN-162: a refused save sets this; the tick is the only way past, and
  // editing the date clears both for the server to re-judge on the next
  // save. Re-armed whenever the dialog is pointed at a different expense —
  // the dialog stays mounted across open/close, so without this a tick
  // given for one expense rode into the next and wrote an audit row for a
  // decision nobody made. (Pre-merge diff review.)
  const dc = useDateConfirm(null);
  React.useEffect(() => { dc.resetFor(`${open}:${effectiveExpense?.id || "new"}`, null); }, [open, effectiveExpense?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    // Refuse here rather than letting the server refuse: the owner has the end
    // date in front of them and can fix it in one move.
    if (recur.enabled && recurOverflow) {
      toast.error("That end date needs more than 120 entries. Choose an earlier end date, or repeat less often.");
      return;
    }
    // The year ceiling was DETECTED and shown, but not enforced — so a
    // count-based schedule ran past it, walked the owner through the
    // confirmation step, and only failed after they committed. Warning about
    // something and then letting it through is worse than not warning: it
    // teaches the owner that the warning does not mean anything.
    if (recur.enabled && recurBeyondCeiling.length > 0) {
      toast.error(`This schedule runs past ${recurCeilingYear}, which is further ahead than expenses can be dated. Shorten it, or repeat less often.`);
      return;
    }
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.payee) { toast.error("Payee is required"); return; }
    if (f.original_amount === "" || isNaN(amt)) { toast.error("Enter an amount"); return; }
    // The approved wording, and the same rule the server applies. (ZEN-276)
    if (!(amt > 0)) { toast.error("Enter what this expense cost, as an amount greater than zero."); return; }
    if (decimalsOf(f.original_amount) > 2) {
      toast.error("Amounts are recorded to the cent — for example 10.01, not 10.005."); return;
    }
    // ── A RATE NOBODY COULD HAVE BEEN CHARGED, AND ONE NOBODY HAS VOUCHED FOR.
    // Both are refused by the server too; refusing here means the owner is told
    // beside the box they typed it in rather than after a failed save. (ZEN-247)
    if (rateVerdict?.refuse) { toast.error(rateRefusedSentence(rateVerdict)); return; }
    if (rateVerdict && !f.rate_confirmed) {
      toast.error("Confirm the exchange rate you entered, or correct it, before saving."); return;
    }
    // Category has no default any more (it opens on a placeholder so nothing is
    // saved unread), so Save has to require it. Without this an expense can be
    // stored with a blank category — worse than the wrong-category bug the
    // placeholder was introduced to prevent.
    // An unclassified expense may be saved WITHOUT answering — correcting a
    // payee typo is not a tax decision, and the server keeps it waiting. But an
    // answer half-given is refused here, in front of the owner, rather than by
    // the server after they commit. (ZEN-114)
    if (isUnclassified && f.eligible_529 === null) {
      if (f.category) { toast.error("Answer whether this expense is 529-eligible before choosing a category"); return; }
    } else if (!f.category && !answersFrozen) { toast.error("Select an expense category"); return; }
    // A locked box cannot be answered here (ZEN-826), so these two checks are
    // the server's to make on a settled expense; it reads a blank draw as yes.
    if (f.eligible_529 === true && (f.draw_from_529 === null || f.draw_from_529 === undefined) && !answersFrozen) {
      toast.error(DRAW_QUESTION_UNANSWERED); return;
    }
    if (!f.payor_type) { toast.error("Select who paid"); return; }
    if (f.payor_type === "party" && !f.payor_party_id) { toast.error("Select which party paid"); return; }
    if (f.aotc_reserved && aotcData?.tax_free_aid > 0 && !f.aotc_scholarship_confirmed) { toast.error("Confirm the scholarship attestation before saving an AOTC-reserved expense"); return; }
    // A computer counts for the AOTC only when needed for a course of study
    // (ZEN-165, asked on screen only). Checked at save, so changing the category
    // after choosing "Partly" cannot skip it (outside review, ZEN-833 round 1).
    if (holdsReservation(f) && isComputerCat && !computerConfirmed) { toast.error(COMPUTER_UNCONFIRMED); return; }
    // A RESERVATION IS NOT SAVED AGAINST A RATE WE DO NOT HAVE YET.
    //
    // The amount set aside for the credit is derived from the expense's US
    // dollar value, and that value is zero until the exchange rate arrives. The
    // clamp below reacts to whatever `usd` currently is — so an owner who
    // changed the currency or the date and hit Save inside that window stored a
    // $0.00 reservation, or one converted at the PREVIOUS rate. Nothing warned
    // them and nothing refused it, because $0.00 is a legitimate state the
    // server accepts: the intended figure was simply gone, silently, from a tax
    // record. The screen already says the rate is pending; this makes Save agree
    // with it. (Pre-merge review — found after the US dollar fix, which is what
    // created the window.)
    // Asked about the RATE, not about the resulting figure. Testing `usd > 0`
    // instead would also refuse a zero-amount expense — a different problem, and
    // one this message would explain wrongly.
    if (f.aotc_reserved && (fxLoading || (!isUSD(f.original_currency) && !(rate > 0)))) {
      toast.error(
        "Wait for the exchange rate before saving this tax-credit reservation \u2014 " +
        "the amount set aside is worked out in US dollars, and it cannot be " +
        "calculated until the rate for this payment date is known.");
      return;
    }
    setBusy(true);
    try {
      // A SAVE THAT CARRIES NO ANSWER MUST NOT LOOK LIKE ONE. The server decides
      // whether this expense leaves the waiting room from what the request
      // actually says, so an unanswered classification is OMITTED entirely
      // rather than sent as the false/true the form happens to hold.
      const classifying = !isUnclassified || f.eligible_529 !== null;
      const payload = {
        ...f, original_amount: amt,
        ...(answersFrozen && f.eligible_529 === true && f.draw_from_529 == null ? { draw_from_529: true } : {}),
        paid_by: ["student", "owner", "529"].includes(f.payor_type) ? f.payor_type : "owner",
        payor_party_id: f.payor_type === "party" ? f.payor_party_id : null,
        payment_source_id: f.payment_source_id || null,
        fx_rate: f.fx_override ? parseFloat(f.fx_rate) : undefined,
        // The dollar figure is the server's to work out, and this form was
        // sending back the one it opened with — a stale number that had no
        // business travelling with an edited amount. What is shown beside the
        // amount field is a live preview (amount x rate); what is SAVED is
        // whatever the server derives. (2026-08-16)
        usd_amount: undefined,
        // Named only when the person chose it or the edit keeps it; otherwise
        // the server picks from the date (ZEN-460, review round 9).
        school_enrollment_id: namedSchool(schoolChosen, keepSchool, f.school_enrollment_id) || null,
        // The period is the server's to decide (ZEN-58); nothing is sent.
        coa_period_id: undefined,
        // Never re-send "draft": the status is the server's to decide, and
        // echoing it back would ask to park an expense the owner just answered.
        status: undefined,
      };
      if (!classifying) {
        delete payload.eligible_529;
        delete payload.draw_from_529;
        delete payload.category;
      }
      if (dc.confirmed) payload.date_confirmed = true;
      let savedId = effectiveExpense?.id;
      const addedThisTry = [];
      if (isEdit) {
        await api.put(`/expenses/${effectiveExpense.id}`, payload);
      } else {
      // A repeating expense takes its documents at creation (owner ruling
      // 2026-09-25). The file is uploaded ONCE and every month names it.
      if (recur.enabled) {
          if (recurSpansYears && !recurCapRef.current) { setRecurCapConfirm(true); setBusy(false); return; }
          recurCapRef.current = false;
          payload.recurrence_interval_months = recur.intervalMonths;
          payload.recurrence_day = recurDay;
          if (recur.endType === "date") payload.recurrence_end_date = recur.endDate;
          else payload.recurrence_occurrences = recur.occurrences;
          // The files go in FIRST, so the save judges the whole schedule
          // against what each document states before any month is written,
          // and attaches every month or none. If a file or the save fails,
          // the files this attempt added are taken back out of the inbox
          // (never erased; one an expense uses is refused), so nothing is
          // left behind. A file the household already held is not touched.
          // (ZEN-724)
          if (receiptFiles.length > 0) {
            const sent = [];
            try {
              for (const rf of receiptFiles) {
                const up = await sendExpenseDocument(api, rf, payload.student_id);
                sent.push(up.id);
                if (!up.reused) addedThisTry.push(up.id);
              }
            } catch (e) {
              await takeBackFromInbox(api, addedThisTry);
              throw e;
            }
            payload.receipt_ids = sent;
          }
        }
        let data;
        try {
          ({ data } = await api.post("/expenses", payload, withSaveKey(saveKey)));
        } catch (e) {
          await takeBackFromInbox(api, addedThisTry);
          throw e;
        }
        if (data?.created) {
          // Say WHAT was created, not just how many. This message used to read
          // "4 recurring expenses created" when the owner had asked for 12 — the
          // server silently discarded every occurrence outside one tax year and
          // the green toast gave no hint (ZEN-112). The dropping is fixed, but a
          // bare count still can't be checked against what was asked for, so the
          // span is spelled out. When a schedule crosses a calendar year the tax
          // years are named too, because that is the part an owner most needs to
          // know for their records.
          const dates = (data.expenses || []).map((e) => e.transaction_date).filter(Boolean).sort();
          const years = [...new Set(dates.map((d) => d.slice(0, 4)))];
          const span = dates.length ? ` — ${fmtDate(dates[0])} to ${fmtDate(dates[dates.length - 1])}` : "";
          const across = years.length > 1 ? `, across tax years ${years.join(" and ")}` : "";
          // The documents were attached to every month by the save itself.
          setReceiptFiles([]);
          toast.success(`${data.created} recurring expenses created${span}${across}`);
          qc.invalidateQueries({ queryKey: ["envelopes"] });
          qc.invalidateQueries({ queryKey: ["accounts-cards-summary"] });
          onSaved?.(); onClose(); return;
        }
        savedId = data?.id;
        // The dialog is now EDITING what it just created (see createdExpense).
        if (data?.id) setCreatedExpense(data);
      }
      if (receiptFiles.length > 0 && savedId) {
        // SAY WHAT HAPPENED, not what was intended. This loop used to catch
        // each failure, carry on, and finish with a green success message —
        // four files with one bad one attached three and said everything
        // worked. That is the same lie as the defect this ticket fixes: the
        // screen reporting intent rather than outcome. (ZEN-79)
        //
        // THE SENTENCES ARE NOT WRITTEN HERE ANY MORE. (ZEN-447) ZEN-446 put
        // the wording for this exact failure in ONE place and wired the gift
        // and distribution screens to it; this box kept its own version, which
        // said the same thing in different words. Two copies of one message
        // drift: the day someone corrects one of them, the other keeps saying
        // the old thing, and a year later the app gives two different accounts
        // of the same failure. So the behaviour below is unchanged and only
        // the words move — out of this file, into lib/uploadOutcome.js.
        const staged = receiptFiles;
        // Through the inbox's own door, attached to this expense (ZEN-724).
        const { ok, failed } = await runUploads(
          staged,
          (rf) => sendExpenseDocument(api, rf, payload.student_id, savedId),
          apiErr,
        );
        // The dialog shows the expense as it now stands, so the files that DID
        // attach are visible beside the error about the one that did not.
        if (ok.length) {
          try {
            const { data: latest } = await api.get(`/expenses/${savedId}`);
            if (latest?.id) setCreatedExpense(latest);
          } catch { /* the toast below still says what happened */ }
        }
        // Only the refused files stay staged, so pressing Save again retries
        // exactly those — and, because the dialog is now editing the expense
        // that was just created, the retry cannot create a second one.
        setReceiptFiles(failed.map((m) => m.file));
        const miss = attachmentFailureMessage({
          noun: "expense",
          total: staged.length,
          attached: ok.length,
          failed,
          // No retry sentence: Maryse found it confusing (ZEN-724, 24 Oct 2026).
          // The box stays open with the refused files still listed.
        });
        if (miss) {
          toast.error(miss.title, { description: miss.description });
          setBusy(false);
          onSaved?.();
          return;
        }
      }
      // SAY WHAT HAPPENED, not just that it saved. "Saved" after answering the
      // 529 question leaves the owner wondering whether the answer took.
      toast.success(
        isUnclassified && classifying
          ? (f.eligible_529
              ? `Classified as 529-eligible — it's out of “${STATUS_META.draft.label}”`
              : `Classified as not 529-eligible — it's out of “${STATUS_META.draft.label}”`)
          : isEdit ? "Expense updated" : "Expense added");
      qc.invalidateQueries({ queryKey: ["envelopes"] });
      onSaved?.();
      onClose();
    } catch (e) {
      if (!dc.applyRefusal(e)) toast.error(apiErr(e));
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto"
        onInteractOutside={(e) => { if (e.target?.closest?.("[data-radix-popper-content-wrapper]") || e.target?.closest?.("[cmdk-root]")) e.preventDefault(); }}>
        <DialogHeader>
          <div className="flex items-center justify-between pr-8">
            <DialogTitle>{isUnclassified ? "Classify this expense" : "Expense"}</DialogTitle>
            <button
              type="button"
              onClick={() => {
                if (aiOpen) { closeAI(); return; }
                const cat = f.category || "expense";
                const payee = f.payee ? ` at ${f.payee}` : "";
                openAI(`About this ${cat} expense${payee}: `);
              }}
              data-testid="exp-ask-ai-pill"
              aria-label="Ask AI"
              aria-expanded={aiOpen}
              title="Ask AI"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-[#034AA2] text-white transition-colors hover:bg-[#023B82]"
            >
              <Sparkle size={16} weight="bold" />
            </button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* ZEN-461: the settled banner, owner-approved copy verbatim — do
              not paraphrase. It used to promise the OPPOSITE ("changing the
              amount will reopen it"); the amount and date are now frozen
              server-side until the settlement is undone. Shown for every
              settled state, not just "reconciled": an expense settled via the
              plan sits at "closed" with a settled reason, and its edits meet
              the same refusals. */}
          {isEdit && (expense?.status === "reconciled"
            || expense?.closed_reason === "settled_privately"
            || expense?.closed_reason === "settled_529") && (
            <div className="sm:col-span-2 flex items-start gap-2 rounded-lg border border-[#F5A623]/40 bg-[#FFF8EC] px-3 py-2.5" data-testid="reconciled-warning">
              <WarningCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
              <p className="text-xs text-[#92600A]">
                <span className="font-bold">This expense has been settled.</span> You can still correct the Payee / Issued by, the Description, the Notes and the Payment account, and you can attach a receipt. Everything else — the Amount, the Transaction date, the Category, Paid by, Reimbursed by, the two 529 answers, School enrollment and COA period — needs the settlement undone first.
              </p>
            </div>
          )}
          <Sel label="Beneficiary" value={f.student_id} onChange={(v) => set("student_id", v)} testid="exp-student" disabled={isEdit}>
            <option value="">Select…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </Sel>
          <SchoolEnrollmentBox choices={schoolChoices?.choices} value={f.school_enrollment_id}
            onChange={(v) => { chooseSchool(); set("school_enrollment_id", v); }}
            studentName={selectedStudent ? (selectedStudent.nickname || (selectedStudent.full_name || "").split(" ")[0]) : ""}
            year={derivedTaxYear} disabled={moneyTied}
            extraOption={keepStoredSchool || moneyTied ? (moneyTied ? (students.find((st) => st.id === effectiveExpense.student_id)?.schools || []) : schools).find((s) => s.id === f.school_enrollment_id) : null} />

          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
              Transaction date
              {/* Scoped deliberately to what the date IS and its FX effect.
                  The timing question this note used to defer is SETTLED
                  (ZEN-331, owner ruling 2026-08-26): the label starts as the
                  year paid and may follow a tied LATER-year withdrawal — and
                  that decision has its own section below, not this tooltip. */}
              <InfoTip testid="exp-date-info" width="w-80">
                <p>The date you actually paid the expense — not when it was invoiced or when the term started.</p>
                <p className="mt-2">If you paid in another currency, this date sets the exchange rate. The app locks in that day&rsquo;s published rate and stores it, so your records stay defensible years later.</p>
              </InfoTip>
            </span>
            <UnifiedDateInput value={f.transaction_date} onChange={(v) => { set("transaction_date", v); dc.clear(); }} testid="exp-date" tentativeToday preset="softFutureDate" assignedTaxYear={derivedTaxYear ? Number(derivedTaxYear) : undefined} onValidation={onDateValidation("transaction_date")} />
            <DateConfirmBanner message={dateFlagMessage(dc.flag, "owner")}
              confirmed={dc.confirmed} onConfirmedChange={dc.setConfirmed}
              testid="exp-date-confirm" />
          </label>
          <Inp label="Payee / Issued by" value={f.payee} onChange={(v) => set("payee", v)} testid="exp-payee" placeholder="University, landlord…" />

          {/* ── WHICH YEAR THIS COST COUNTS TOWARD. (ZEN-331)
              Rendered only when there is a real decision to make: the cost is
              matched to a withdrawal recorded in a LATER year than it was
              paid, or its label already follows one. The server owns every
              fact here — the section renders what GET /expenses/{id}/tax-year
              answers and posts its pins back verbatim, so the decision is
              always about the record the account owner was shown. */}
          {isEdit && yearOpts && (yearOpts.follows || (yearOpts.options || []).length > 0) && (
            <div className="col-span-2 rounded-md border border-[#E9E9EB] bg-[#F7F8F9] px-3 py-2" data-testid="exp-tax-year-section">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Tax year</div>
              {yearOpts.follows ? (
                <div className="mt-1">
                  <p className="text-xs text-[#374151]" data-testid="exp-tax-year-follows">
                    This cost counts toward <b>{yearOpts.label}</b> — the year of the withdrawal that repaid it. It was paid in {yearOpts.paid_year}.
                  </p>
                  {/* The reason is required in BOTH directions — moving a
                      filed position back is the same kind of decision as
                      moving it forward, and the server refuses a wordless
                      move. (ZEN-331, threat T9.) */}
                  <label className="mt-1 block">
                    <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Why move it back?</span>
                    <textarea rows={2} maxLength={2000} value={yearMoveNote}
                      onChange={(ev) => setYearMoveNote(ev.target.value)}
                      placeholder="For example: this cost was not part of what that withdrawal repaid."
                      className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
                      data-testid="exp-tax-year-back-note" />
                  </label>
                  <button type="button" data-testid="exp-tax-year-move-back"
                    className="mt-1.5 text-xs font-semibold text-[#7D3306] hover:underline disabled:text-[#9CA3AF]"
                    disabled={yearMoveBusy || !yearMoveNote.trim()}
                    onClick={() => moveTaxYear("paid-year")}>
                    Count it toward {yearOpts.paid_year} instead (the year it was paid)
                  </button>
                </div>
              ) : (
                (yearOpts.options || []).map((opt) => (
                  <div key={opt.withdrawal_id} className="mt-1" data-testid={`exp-tax-year-option-${opt.withdrawal_id}`}>
                    <p className="text-xs text-[#374151]">
                      Paid in <b>{yearOpts.paid_year}</b>, and matched to a withdrawal recorded in <b>{opt.year}</b>{opt.effective_date ? ` (${fmtDate(opt.effective_date)})` : ""}.
                      {Number(yearOpts.label) === Number(opt.year)
                        ? " It already counts toward that year."
                        : ` It currently counts toward ${yearOpts.label ?? yearOpts.paid_year}.`}
                    </p>
                    <label className="mt-1 block">
                      <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Why do these belong together?</span>
                      <textarea rows={2} maxLength={2000} value={yearMoveNote}
                        onChange={(ev) => setYearMoveNote(ev.target.value)}
                        placeholder="For example: paid the university early for next year's study."
                        className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
                        data-testid="exp-tax-year-note" />
                    </label>
                    <button type="button" data-testid={`exp-tax-year-move-${opt.withdrawal_id}`}
                      className="mt-1 text-xs font-semibold text-[#0B6E99] hover:underline disabled:text-[#9CA3AF]"
                      disabled={yearMoveBusy || !yearMoveNote.trim()}
                      onClick={() => moveTaxYear(opt.withdrawal_id, opt.effective_date, opt.amount_usd)}>
                      {Number(yearOpts.label) === Number(opt.year)
                        ? `Confirm — it counts toward ${opt.year}`
                        : `Count it toward ${opt.year} — it will move on your year totals and tax documents`}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ---- Eligibility + Recurrence toggle (50/50) ---- */}
          <Sel label="Is this expense 529-eligible?"
            value={f.eligible_529 === null || f.eligible_529 === undefined ? "" : (f.eligible_529 ? "yes" : "no")}
            className={f.eligible_529 === null || f.eligible_529 === undefined ? "text-[#9CA3AF]" : ""}
            onChange={(v) => {
            if (v === "") return;   // the placeholder is not an answer
            const isEligible = v === "yes";
            const irsCats = irsCategoriesFor(studentGroup);
            if (isEligible) {
              // Answering yes does NOT also answer "draw from the plan" — that is
              // a second decision about the owner's money, and the two used to
              // move together silently. It opens unanswered too.
              setF(p => ({ ...p, eligible_529: true,
                           draw_from_529: p.draw_from_529 === null || p.draw_from_529 === undefined ? null : p.draw_from_529,
                           category: irsCats.includes(p.category) ? p.category : "" }));
            } else {
              // Switching eligibility always clears to the placeholder — the two
              // category lists are unrelated, so carrying one over would be wrong.
              // An expense that does not qualify is never drawn from the plan.
              setF(p => ({ ...p, eligible_529: false, draw_from_529: false, category: "" }));
            }
          }} testid="exp-eligible" disabled={answersFrozen}>
            {(f.eligible_529 === null || f.eligible_529 === undefined) && <option value="" disabled>Choose an answer</option>}
            <option value="yes" className="text-[#1f2937]">Yes — 529-eligible</option>
            <option value="no" className="text-[#1f2937]">No — not 529-eligible</option>
          </Sel>
          {!isEdit ? (
            <div>
              <span className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
                Recurrence
                <InfoTip testid="exp-recurrence-info" text={f.eligible_529 ? "Choose Repeating for an expense that recurs on a schedule — like monthly rent or a quarterly fee. One-time is the default." : "Choose Repeating for an expense that recurs on a schedule — like a monthly music lesson or an annual club fee. One-time is the default."} />
              </span>
              <div className="flex overflow-hidden rounded-lg border border-[#C2C9D1] w-fit">
                <button type="button" onClick={() => setRecur(r => ({...r, enabled: false}))}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${!recur.enabled ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}
                  data-testid="recur-onetime">One-time</button>
                <button type="button" onClick={() => setRecur(r => ({...r, enabled: true}))}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${recur.enabled ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}
                  data-testid="recur-repeating">Repeating</button>
              </div>
            </div>
          ) : <div />}

          {/* Recurrence expanded panel — full-width below trigger row */}
          {!isEdit && recur.enabled && (
            <div className="sm:col-span-2">
              <div className="space-y-3 rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-[#374151]">Every</span>
                  <input type="number" min="1" max="24" value={recur.intervalMonths}
                    onChange={(e) => setRecur(r => ({...r, intervalMonths: Math.min(24, Math.max(1, parseInt(e.target.value)||1))}))}
                    className="h-9 w-16 rounded-md border border-[#C2C9D1] px-2 text-center text-sm" data-testid="recur-interval" />
                  <span className="text-[#374151]">month{recur.intervalMonths > 1 ? "s" : ""} on the</span>
                  <select value={recurDay} onChange={(e) => setRecur(r => ({...r, day: parseInt(e.target.value)}))}
                    className="h-9 rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid="recur-day">
                    {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}{d===1?"st":d===2?"nd":d===3?"rd":"th"}</option>
                    ))}
                  </select>
                  {recurLabel && <span className="rounded-full bg-[#034AA2]/10 px-2.5 py-0.5 text-xs font-semibold text-[#034AA2]">{recurLabel}</span>}
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 text-sm">
                    <input type="radio" name="recur-end" checked={recur.endType === "date"} onChange={() => setRecur(r => ({...r, endType: "date"}))} />
                    <span className="w-12 text-[#374151]">Until</span>
                    {recur.endType === "date" && (
                      <UnifiedDateInput value={recur.endDate} onChange={(v) => setRecur(r => ({...r, endDate: v}))}
                        testid="recur-end-date" hideToday hideHint onValidation={onDateValidation("recur_end")} />
                    )}
                  </label>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="radio" name="recur-end" checked={recur.endType === "occurrences"} onChange={() => setRecur(r => ({...r, endType: "occurrences"}))} />
                    <span className="w-12 text-[#374151]">After</span>
                    {recur.endType === "occurrences" && (
                      <span className="flex items-center gap-2">
                        {/* CLAMPED BOTH WAYS. The native max does not constrain a
                            TYPED value, and nothing checked validity on save — so
                            typing 121 previewed 120 occurrences and then sent 121,
                            which the server refuses. The preview and the request
                            have to be the same number. */}
                        <input type="number" min="2" max="120" value={recur.occurrences}
                          onChange={(e) => setRecur(r => ({...r, occurrences: Math.min(120, Math.max(2, parseInt(e.target.value)||2))}))}
                          className="h-9 w-16 rounded-md border border-[#C2C9D1] px-2 text-center text-sm" data-testid="recur-occurrences" />
                        <span className="text-sm text-[#374151]">occurrences</span>
                      </span>
                    )}
                  </label>
                </div>
                {recurOverflow && (
                  <div className="rounded-md border border-[#B91C1C]/30 bg-[#B91C1C]/5 px-3 py-2" data-testid="recur-overflow">
                    <span className="text-xs font-bold text-[#B91C1C]">That end date needs more than 120 entries. </span>
                    <span className="text-xs text-[#374151]">
                      This app creates at most 120 at once. Choose an earlier end date, or repeat less often.
                    </span>
                  </div>
                )}
                {recurPreview.length > 0 && !recurOverflow && (
                  <div className="space-y-1.5" data-testid="recur-preview">
                    {/* A whole year is listed (owner, 2026-09-28); longer schedules say how many more. */}
                    <div className="rounded-md border border-[#034AA2]/20 bg-[#034AA2]/5 px-3 py-2">
                      <span className="text-xs font-bold text-[#034AA2]">Will create {recurPreview.length} expenses: </span>
                      <span className="text-xs text-[#374151]" data-testid="recur-preview-dates">
                        {recurPreview.slice(0, 12).map(d => d.toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"})).join(" · ")}
                        {recurPreview.length > 12 ? ` · +${recurPreview.length - 12} more` : ""}
                      </span>
                    </div>
                    {recurBeyondCeiling.length > 0 && (
                      <div className="rounded-md border border-[#F5A623]/30 bg-[#F5A623]/10 px-3 py-2 text-xs font-semibold text-[#7D5A00]" data-testid="recur-beyond-ceiling">
                        {recurBeyondCeiling.length} of these fall after {recurCeilingYear}, which is as far ahead as expenses can be dated. Shorten the schedule — it won&apos;t save as it stands.
                      </div>
                    )}
                    {recurSpansYears && (
                      // Informational, not a warning: crossing a tax year is
                      // normal for a September-to-August academic lease and
                      // nothing is lost. Amber is reserved for "needs your
                      // attention"; this is neutral, so it reads as blue.
                      <div className="rounded-md border border-[#034AA2]/30 bg-[#034AA2]/10 px-3 py-2 text-xs font-semibold text-[#034AA2]" data-testid="recur-spans-years">
                        This schedule runs across tax years {recurYears.join(" and ")}. Each expense is filed under the year it&apos;s paid.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---- Category + Description (50/50) ---- */}
          {/* NEITHER LIST UNTIL THE QUESTION IS ANSWERED. The 529-eligible list
              comes from the tax rules and the non-529 list is the family's own,
              so showing either one already suggests an answer to the question
              above it. (ZEN-114) */}
          {f.eligible_529 === null || f.eligible_529 === undefined ? (
            <label className="block" data-testid="exp-category-locked">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Category</span>
              <div className="flex h-10 w-full items-center rounded-md border border-dashed border-[#C2C9D1] bg-[#F8FAFC] px-3 text-xs text-[#6b7280]">
                Answer the 529 question first
              </div>
            </label>
          ) : f.eligible_529 ? (
            <Sel label="Category" value={f.category} onChange={(v) => set("category", v)} testid="exp-category"
              disabled={answersFrozen} className={f.category ? "" : "text-[#9CA3AF]"}>
              {!f.category && <option value="" disabled>Select an expense category</option>}
              {irsCategoriesFor(studentGroup).map(c => <option key={c} value={c} className="text-[#1f2937]">{c}</option>)}
            </Sel>
          ) : (
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
                Category
                <InfoTip testid="noneligible-category-info" width="w-80">Not everything a family pays for is a 529-eligible expense — the IRS specifically excludes things like transportation, insurance, and other personal or family expenses. Record those here when someone wants them tracked or reimbursed by another family member; they stay out of every 529 tax report and never touch your tax math. Categories you add — here, or in Settings — are kept for this beneficiary and appear at the top of this list; choose &ldquo;Other — enter manually&rdquo; for a one-off you will not use again.</InfoTip>
              </span>
              <select value={f.category} onChange={(e) => {
                const v = e.target.value;
                // Both of these open something instead of choosing something, so
                // neither writes to the form. The picker is controlled by
                // f.category, so it snaps back to what was already chosen.
                if (v === ADD_CATEGORY_OPTION) { categoryAttempt.current += 1; setAddCatOpen(true); return; }
                if (v === "Other — enter manually") { setCustomCatOpen(true); return; }
                set("category", v);
              }} data-testid="exp-category" disabled={answersFrozen}
                className={`h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 disabled:bg-[#E9E9EB] ${f.category ? "" : "text-[#9CA3AF]"}`}>
                {!f.category && <option value="" disabled>Select an expense category</option>}
                {(selectedStudent?.noneligible_categories || []).map(c => <option key={c} value={c} className="text-[#1f2937]">{c}</option>)}
                {/* THE NAME THE RECORD ACTUALLY HOLDS, when the list does not
                    offer it — a one-off the owner typed, or a category later
                    deleted in Settings. Without this option the picker matches
                    nothing and silently shows its FIRST entry instead, so the
                    screen names a category nobody chose and keeps naming it on
                    every reopen. The rule and the wording below are the shared
                    ones the receipt review screen already uses. (ZEN-685) */}
                {isOffListCategory(f.category, selectedStudent?.noneligible_categories) && (
                  <option value={f.category} className="text-[#1f2937]">{f.category}</option>
                )}
                {/* Not "…in Settings" any more: this no longer goes to Settings.
                    Naming a destination you are not taken to was the old bug's
                    signature. (ZEN-56, owner-approved wording.) */}
                <option value={ADD_CATEGORY_OPTION}>{ADD_CATEGORY_LABEL}</option>
                <option value="Other — enter manually">Other — enter manually</option>
              </select>
              {isOffListCategory(f.category, selectedStudent?.noneligible_categories) && (
                <div className="mt-0.5 text-[10px] font-semibold text-[#034AA2]" data-testid="exp-category-custom-hint">{offListCategoryHint(f.category)}</div>
              )}
              {addCatOpen && (() => {
                // THE CATEGORY BELONGS TO THE BENEFICIARY IT WAS ADDED FOR, and to
                // this opening of the box under this answer to the 529 question.
                // The pickers stay live while the save is in flight; a reply that
                // comes back after any of them changed updates the list and
                // chooses nothing. (ZEN-56, adversarial review round 4.)
                const opening = categoryAttempt.current;
                return (
                  <AddCategoryInline
                    key={opening}
                    student={selectedStudent}
                    isStillWanted={(savedFor) => savedFor === studentIdNow.current
                      && opening === categoryAttempt.current
                      && eligibilityNow.current === false}
                    onCancel={() => { categoryAttempt.current += 1; setAddCatOpen(false); }}
                    // THE NAME THE SERVER KEPT, not the one that was typed. It
                    // trims and shortens; selecting the typed one would leave the
                    // box holding a category that is not on the list.
                    onChosen={(stored) => { set("category", stored); setAddCatOpen(false); }} />
                );
              })()}
            </label>
          )}
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
              Description
              <InfoTip testid="exp-description-info" text={f.eligible_529 ? "Optional — add a note only if you want to remember more about this expense (e.g., \u2018macroeconomics textbook\u2019, \u2018laptop charger\u2019). It\u2019s just for your own reference and isn\u2019t required." : "Optional — add a note only if you want to remember more about this expense (e.g., \u2018soccer registration\u2019, \u2018art supplies\u2019). It\u2019s just for your own reference and isn\u2019t required."} />
            </span>
            <input type="text" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="exp-description" placeholder="What was this for?"
              className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-3 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30" />
          </label>
          {/* Category tooltip */}
          {(() => {
            const tip = CATEGORY_TOOLTIPS[`${studentGroup}:${f.category}`] || CATEGORY_TOOLTIPS[f.category];
            return tip ? (
              <div className="sm:col-span-2 -mt-1 flex items-start gap-1.5 rounded-md border border-[#C2C9D1] bg-[#F4F7FB] px-2.5 py-2 text-[11px] text-[#6b7280]" data-testid="category-tooltip">
                <Info size={14} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" /> <span>{tip}</span>
              </div>
            ) : null;
          })()}
          {/* Statutory cap warnings */}
          {(() => {
            const capMap = {
              k12_tuition: { group: "k12", category: "Tuition" },
            };
            const active = statutoryCaps.filter((c) => {
              const m = capMap[c.type];
              if (!m) return false;
              if (m.group && f.expense_group !== m.group) return false;
              if (f.category !== m.category) return false;
              return f.eligible_529 && f.draw_from_529;
            });
            return active.map((c) => {
              const isOver = c.over;
              const willExceed = usd > 0 && (c.claimed + usd) > c.cap + 0.005;
              const borderClr = isOver ? "border-[#DC2626]" : willExceed ? "border-[#D97706]" : "border-[#C2C9D1]";
              const bgClr = isOver ? "bg-[#DC2626]/5" : willExceed ? "bg-[#D97706]/5" : "bg-[#F4F7FB]";
              return (
                <div key={c.type} className={`sm:col-span-2 -mt-1 rounded-md border ${borderClr} ${bgClr} p-2.5 text-xs`} data-testid={`cap-${c.type}`}>
                  <div className="font-semibold text-[#374151]">{c.label} — {c.scope === "lifetime" ? "lifetime" : "annual"} cap</div>
                  <div className="mt-0.5 text-[#6b7280]">
                    Cap {money(c.cap, "USD")} · {money(c.claimed, "USD")} used · <span className={c.remaining < 0 ? "text-[#DC2626]" : "text-[#22C55E]"}>{money(c.remaining, "USD")} remaining</span>
                  </div>
                  {usd > 0 && <div className="mt-0.5 text-[#6b7280]">This expense: {money(usd, "USD")} → new total {money(c.claimed + usd, "USD")}</div>}
                  {isOver && <div className="mt-1 font-semibold text-[#DC2626]">⚠ Already over the cap by {money(Math.abs(c.remaining), "USD")}</div>}
                  {!isOver && willExceed && <div className="mt-1 font-semibold text-[#D97706]">⚠ This expense would exceed the cap by {money(c.claimed + usd - c.cap, "USD")}</div>}
                  <div className="mt-1 text-[10px] text-[#9CA3AF]">{c.description}</div>
                </div>
              );
            });
          })()}

          {/* Paid by + Payment Account — side by side */}
          <div>
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Paid by</span>
            <div className="flex overflow-hidden rounded-lg border border-[#C2C9D1] w-full">
              <button type="button" onClick={() => { set("payor_type", "student"); set("payor_party_id", ""); }} disabled={answersFrozen} data-testid="exp-paidby-student"
                className={`flex-1 disabled:cursor-not-allowed disabled:opacity-60 px-2 py-2 text-xs font-semibold transition-colors text-center ${f.payor_type === "student" ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>Student</button>
              <button type="button" onClick={() => { set("payor_type", "owner"); set("payor_party_id", ""); }} disabled={answersFrozen} data-testid="exp-paidby-owner"
                className={`flex-1 disabled:cursor-not-allowed disabled:opacity-60 border-l border-[#C2C9D1] px-2 py-2 text-xs font-semibold transition-colors text-center ${f.payor_type === "owner" ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>Owner</button>
              {f.eligible_529 === true && (
              <button type="button" onClick={() => { set("payor_type", "529"); set("payor_party_id", ""); }} disabled={answersFrozen} data-testid="exp-paidby-529"
                className={`flex-1 disabled:cursor-not-allowed disabled:opacity-60 border-l border-[#C2C9D1] px-2 py-2 text-xs font-semibold transition-colors text-center ${f.payor_type === "529" ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>529 Direct</button>
              )}
              <button type="button" onClick={() => { if (f.payor_type !== "party") { set("payor_type", "party"); set("payor_party_id", ""); } }} disabled={answersFrozen} data-testid="exp-paidby-other"
                className={`flex-1 disabled:cursor-not-allowed disabled:opacity-60 border-l border-[#C2C9D1] px-2 py-2 text-xs font-semibold transition-colors text-center ${f.payor_type === "party" ? "bg-[#034AA2] text-white" : "bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>Other</button>
            </div>
            {/* Resolved payer caption */}
            {f.payor_type === "student" && <p className="mt-1 text-xs text-[#6b7280]">· {studentNick}</p>}
            {f.payor_type === "owner" && <p className="mt-1 text-xs text-[#6b7280]">· {parties?.owner?.nickname || "Owner"}</p>}
            {f.payor_type === "party" && f.payor_party_id && <p className="mt-1 text-xs text-[#6b7280]">· {(parties?.custom || []).find(p => p.id === f.payor_party_id)?.nickname || ""}</p>}
            {/* Other — inline picker */}
            {f.payor_type === "party" && (
              <div className="mt-1.5 rounded-md border border-[#C2C9D1] bg-white" data-testid="exp-paidby-other-picker">
                {(parties?.custom || []).map((p) => (
                  <button key={p.id} type="button" onClick={() => set("payor_party_id", p.id)} disabled={answersFrozen} data-testid={`exp-paidby-party-${p.id}`}
                    className={`block w-full disabled:cursor-not-allowed disabled:opacity-60 px-3 py-1.5 text-left text-sm transition-colors ${f.payor_party_id === p.id ? "bg-[#034AA2]/10 font-semibold text-[#034AA2]" : "text-[#374151] hover:bg-[#E9E9EB]"}`}>
                    {p.nickname}
                  </button>
                ))}
                <a href="/settings?tab=parties" target="_blank" rel="noreferrer" data-testid="exp-paidby-add-link"
                  className="flex w-full items-center gap-1.5 border-t border-[#C2C9D1] px-3 py-1.5 text-sm font-semibold text-[#034AA2] hover:bg-[#034AA2]/5">
                  <Plus size={12} weight="bold" /> Add payer
                </a>
              </div>
            )}
          </div>

          <div>
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
              Payment Account
              <InfoTip testid="exp-payment-acct-info" text="A record of how this expense was funded — informational only. It doesn&rsquo;t affect who gets reimbursed." />
            </span>
            {paidByPlan ? (
              /* PAID DIRECTLY BY THE 529 PLAN — a read-only line, not a picker.
                 The plan is not a bank account or a card, and it is not kept
                 with them, so there is no id here that any other screen or the
                 tax packet could look up. Writing the plan's id into the
                 payment-account field would leave every one of those readers
                 with a name they cannot resolve — a blank where an account
                 should be. Saying it in words and recording NO payment account
                 is the unambiguous version: the record already says the plan
                 paid, and which plan is on the matching withdrawal. */
              <div className="flex min-h-10 items-center rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-3 py-2 text-sm text-[#374151]" data-testid="exp-payment-529">
                <PaidByPlanText label={fund529Label} nick={studentNick} />
              </div>
            ) : !psAddOpen ? (
              <>
                <select value={f.payment_source_id || ""} onChange={(e) => onSelectPs(e.target.value)} data-testid="exp-payment-source"
                  className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30">
                  <option value="">— Not specified —</option>
                  {visibleSources.map((s) => {
                    const lbl = `${psLabel(s, psOwnerName(s))}${s.archived ? " (archived)" : ""}`;
                    return <option key={s.id} value={s.id}>{lbl}</option>;
                  })}
                  <option value="__add__">＋ Add payment account…</option>
                </select>
                {noAccountsForPayor && (
                  <div className="mt-2 rounded-md bg-[#E9E9EB] px-2.5 py-1.5 text-xs text-[#6b7280]" data-testid="exp-payment-none-for-payor">
                    No accounts on file for {f.payor_type === "owner" ? (parties?.owner?.nickname || "the account owner") : studentNick}. Add one below, or leave this as &ldquo;Not specified&rdquo;.
                  </div>
                )}
                {legacyHint && (
                  <div className="mt-2 rounded-md bg-[#E9E9EB] px-2.5 py-1.5 text-xs text-[#6b7280]" data-testid="exp-legacy-payment">
                    Legacy record (read-only): {expense.payment_method || "—"}{legacyBank ? ` · ${legacyBank.label}` : ""}
                  </div>
                )}
              </>
            ) : (
              <PsInlineAdd types={psTypes}
                ownerName={f.payor_type === "student" ? (student?.full_name || "") : (parties?.owner?.full_name || parties?.owner?.nickname || "")}
                holderType={f.payor_type === "student" ? "student" : "owner"}
                holderStudentId={f.payor_type === "student" ? f.student_id : null}
                holderName={f.payor_type === "student" ? studentNick : (parties?.owner?.nickname || "you")}
                onCancel={() => setPsAddOpen(false)}
                onSaved={(newId, cur) => { setPsAddOpen(false); psTouched.current = true; qc.invalidateQueries({ queryKey: ["accounts-cards"] }); setF((p) => withRateInputs(p, { payment_source_id: newId, original_currency: cur || p.original_currency })); }} />
            )}
          </div>

          {/* amount + currency + FX */}
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
              Currency
              <InfoTip testid="exp-currency-info" text={CURRENCY_FIELD_HELP} />
            </span>
            {/* Clearing the hand-entered rate is handled by `set` for every field
                that invalidates it — see withRateInputs above. */}
            <CurrencySelect value={f.original_currency} testid="exp-currency"
              onChange={(v) => set("original_currency", v)} />
          </label>
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">Amount</span>
            <MoneyInput currency={curCode(f.original_currency, "USD")}
              value={f.original_amount}
              onChange={(v) => set("original_amount", v)}
              testid="exp-amount" />
          </label>

          {!isUSD(f.original_currency) && (
          <div className="sm:col-span-2 rounded-lg border bg-[#E9E9EB]/50 p-3 border-[#C2C9D1]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <InfoTip testid="exp-fx-box-info" width="w-80" text={CONVERSION_BOX_TIP} />
                Currency conversion
                <span className="text-[#6b7280] font-normal" data-testid="exp-fx-heading">{fxHeadingText}</span>
                {boxIsEstimate && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700" data-testid="exp-fx-estimate-chip">Awaiting final exchange rate</span>
                )}
                {/* FX corrected chip — shows when migration archived the revision log */}
                {expense?.fx_revision_log_legacy?.length > 0 && !expense?.fx_review_ack && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700" data-testid="exp-fx-corrected-chip">FX corrected — review</span>
                )}
              </div>
              <label className="flex items-center gap-1.5 text-xs font-medium">
                <input type="checkbox" checked={f.fx_override} onChange={(e) => {
                  set("fx_override", e.target.checked);
                  if (!e.target.checked) { setFxUsdField(""); setFxUnavailable(false); }
                  else {
                    // Pre-fill USD field from current computed amount
                    const r = parseFloat(f.fx_rate) || (boxFx?.rate || 0);
                    const a = parseFloat(f.original_amount) || 0;
                    setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                  }
                }} data-testid="exp-fx-override" />
                Manual override
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              {isUSD(f.original_currency) ? (
                <span className="text-[#6b7280]">USD expense — no conversion.</span>
              ) : f.fx_override ? (
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-sm">
                    <span className="text-xs font-semibold text-[#6b7280]">Rate</span>
                    <input
                      type="number" step="0.0001"
                      value={f.fx_rate}
                      onChange={(e) => {
                        set("fx_rate", e.target.value);
                        const r = parseFloat(e.target.value) || 0;
                        const a = parseFloat(f.original_amount) || 0;
                        setFxUsdField(r > 0 && a > 0 ? (a * r).toFixed(2) : "");
                      }}
                      data-testid="exp-fx-rate"
                      placeholder="e.g., 0.7213"
                      className="h-8 w-28 rounded border border-[#C2C9D1] px-2 text-sm"
                    />
                  </label>
                  <span className="text-[#9aa1a9] text-xs font-medium">or</span>
                  <MoneyInput currency="USD" size="sm" className="!w-36"
                    value={fxUsdField}
                    onChange={(v) => {
                      setFxUsdField(v);
                      const u = parseFloat(v) || 0;
                      const a = parseFloat(f.original_amount) || 0;
                      set("fx_rate", u > 0 && a > 0 ? String((u / a).toFixed(7)) : "");
                    }}
                    testid="exp-fx-usd-direct"
                    placeholder="e.g., 7553.94"
                  />
                </div>
              ) : (
                <span className="flex items-center gap-2 text-[#6b7280]">
                  {fxLoading ? <ArrowsClockwise size={14} className="animate-spin" /> : null}
                  <span data-testid="exp-fx-rate-line">{boxFx ? `1 ${curCode(f.original_currency)} = ${boxFx.rate} USD · ${boxFx.source === "treasury" ? "US Treasury" : "ECB"} · ${boxFx.rate_date}` : boxWaiting ? "" : "Fetching rate…"}</span>
                </span>
              )}
              <span className="ml-auto rounded-md bg-[#034AA2] px-3 py-1 font-bold text-white tnum" data-testid="exp-usd-preview">
                {/* NO RATE, NO FIGURE. Not only once the lookup has failed: while it is
                    still running (or stalled) there is no rate either, and "$0.00"
                    is a dollar figure nobody worked out. (ZEN-555, review round 3.) */}
                = {!isUSD(f.original_currency) && !(rate > 0) ? "—" : money(usd, "USD")}
              </span>
            </div>
            {boxWaiting && !isUSD(f.original_currency) && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="exp-fx-waiting">
                {NO_RATE_YET}
              </div>
            )}
            {/* ── IS THE TYPED RATE BELIEVABLE? (ZEN-247)
                Sits directly under the rate box, so the sentence is beside the
                number it is about. Two states, and the difference is whether
                there is a way forward from here: a rate more than 10x off the
                published one is refused outright and offers no tick, because
                offering one would suggest the save could still go through. */}
            {rateVerdict && (
              rateVerdict.refuse ? (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-[#B91C1C]/35 bg-[#FEF2F2] px-2.5 py-2 text-[11px] leading-relaxed text-[#7F1D1D]" data-testid="exp-fx-rate-refused">
                  <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0 text-[#B91C1C]" />
                  <span>{rateRefusedSentence(rateVerdict)}</span>
                </div>
              ) : (
                <div className="mt-2 rounded-md border border-[#D97706]/35 bg-[#FFFBEB] px-2.5 py-2 text-[11px] leading-relaxed text-[#92400E]" data-testid="exp-fx-rate-confirm">
                  <div className="flex items-start gap-2">
                    <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
                    <span>{rateConfirmSentence(rateVerdict)}</span>
                  </div>
                  <label className="mt-2 flex items-start gap-2 font-semibold">
                    <input type="checkbox" className="mt-0.5" checked={!!f.rate_confirmed}
                      onChange={(e) => set("rate_confirmed", e.target.checked)}
                      data-testid="exp-fx-rate-confirm-tick" />
                    <span>Yes, that is the rate my bank charged me.</span>
                  </label>
                </div>
              )
            )}
            {/* Override substantiation note */}
            {f.fx_override && !isUSD(f.original_currency) && (
              fxUnavailable ? (
                <div className="mt-2 flex items-start gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-2.5 py-1.5 text-[11px] text-[#92400E]" data-testid="exp-fx-unavailable">
                  <Warning size={12} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
                  {noAutomaticRateFor(f.original_currency)}
                </div>
              ) : attachments.length > 0 ? (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[#077A39]" data-testid="exp-fx-override-documented">
                  <CheckCircle size={12} weight="fill" /> Rate backed by an attached document.
                </div>
              ) : (
                <div className="mt-2 text-[11px] text-[#92400E]" data-testid="exp-fx-override-nudge">
                  Manual rate in use — attach the statement or receipt that shows this amount (for example, your credit-card statement&apos;s US-dollar figure) so the rate is documented.
                </div>
              )
            )}
            {/* FX corrected detail */}
            {expense?.fx_revision_log_legacy?.length > 0 && !expense?.fx_review_ack && (() => {
              const legacy = expense.fx_revision_log_legacy;
              const last = legacy[legacy.length - 1];
              const oldRate = last?.new_rate;
              const oldUsd = oldRate && expense.original_amount ? (parseFloat(expense.original_amount) * parseFloat(oldRate)).toFixed(2) : null;
              return (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="exp-fx-corrected-detail">
                  {oldUsd ? (
                    <>Exchange rate corrected to the transaction-date basis. {money(Number(oldUsd), "USD")} → {money(expense.usd_amount, "USD")}.</>
                  ) : (
                    <>Exchange rate corrected to the transaction-date basis on {expense.fx_rate_date}.</>
                  )}
                </div>
              );
            })()}
            {/* FX estimate detail */}
            {boxIsEstimate && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="exp-fx-estimate-detail">
                {/* Says both things a final rate unlocks (ZEN-834's one rule);
                    wording approved by Maryse, 2 Nov 2026 (ZEN-334). */}
                {ESTIMATE_RATE_NOTE}
              </div>
            )}
          </div>
          )}

          {/* routing flow — draw from 529 + reimbursement */}
          {f.eligible_529 && (
          <div className="sm:col-span-2 rounded-lg border border-[#C2C9D1] bg-white p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Full width when the third answer is listed: its label is
                    long and was cut off at half width (ZEN-833). */}
                <label className={`block ${reserveOffered || drawAnswer === "reserve" || reserveWaitsForRate ? "sm:col-span-2" : ""}`} data-testid="exp-draw-field">
                  <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
                    {DRAW_QUESTION}
                    <InfoTip testid="exp-draw-info" text="Sometimes you shouldn&rsquo;t. Paying out of pocket can preserve up to $4,000 of tuition for the American Opportunity Tax Credit (worth up to $2,500), or apply when a cost is above a 529 cap or already covered by a tax-free scholarship. See the Help Center." />
                  </span>
                  {/* Unanswered opens blank here too: whether to draw on the plan
                      is a second decision about the owner's money, and answering
                      the eligibility question above does not answer it. */}
                  <select value={drawAnswer}
                    onChange={(e) => chooseDrawAnswer(e.target.value)} data-testid="exp-draw" disabled={answersFrozen && !reserveOffered && drawAnswer !== "reserve"}
                    className={`h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 disabled:bg-[#E9E9EB] ${drawAnswer === "" ? "text-[#9CA3AF]" : ""}`}>
                    {drawAnswer === "" && <option value="" disabled>Choose an answer</option>}
                    {/* The three answers in the order Maryse approved, 31 Oct 2026. */}
                    <option value="yes" className="text-[#1f2937]" disabled={frozenDraw === false}>{DRAW_YES_LABEL}</option>
                    <option value="no" className="text-[#1f2937]" disabled={frozenDraw === true}>{DRAW_NO_LABEL}</option>
                    {(reserveOffered || drawAnswer === "reserve") && (
                      <option value="reserve" className="text-[#1f2937]" data-testid="exp-draw-reserve"
                        disabled={isComputerCat && !computerConfirmed && drawAnswer !== "reserve"}>{RESERVE_ANSWER_LABEL}</option>
                    )}
                  </select>
                  {drawAnswer === "reserve" && (
                    <p className="mt-1 text-xs text-[#374151]" data-testid="exp-reserve-line">{reserveLine(reserve)}</p>
                  )}
                  {reserveWaitsForRate && (
                    <p className="mt-1 text-xs text-[#6b7280]" data-testid="exp-reserve-waits-for-rate">{RESERVE_WAITS_FOR_RATE}</p>
                  )}
                </label>
                {f.draw_from_529 === false && (
                  <ReimbursedByField locked={answersFrozen} f={f} set={set} parties={parties} addReimburserOpen={addReimburserOpen} setAddReimburserOpen={setAddReimburserOpen} newReimburserNick={newReimburserNick} setNewReimburserNick={setNewReimburserNick} qc={qc} />
                )}
              {f.draw_from_529 && (() => {
                const RB_CATS = ["Food", "Housing", "Rent / Room & Board", "Room & Board"];
                const isRB = RB_CATS.includes(f.category);
                return (
                  <>
                    {/* A STATEMENT, NOT A FIELD — and the old infotip died with it.
                        It told people that a December payment for a January term
                        should be set to the LATER year, which is the opposite of
                        the rule the account owner settled: the year you PAY is
                        the year it counts toward. (ZEN-172) */}
                    <div className="block">
                      <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
                        Counts toward tax year
                        <InfoTip testid="exp-taxyear-info" text="The date you paid decides which tax year this cost counts toward. To keep the tax treatment safe, take the matching 529 withdrawal during that same calendar year." />
                      </span>
                      <div data-testid="exp-taxyear"
                        className="flex h-10 w-full items-center rounded-md border border-[#E9E9EB] bg-[#F7F8FA] px-3 text-sm text-[#374151]">
                        {derivedTaxYear || "—"}
                        <span className="ml-2 text-[11px] text-[#9aa1a9]">set by the date paid</span>
                      </div>
                    </div>
                    {/* ZEN-793: its own full-width row, and a box that grows, so a
                        long period name wraps inside it; the note flows after the text. */}
                    {isRB ? (
                      <div className="block sm:col-span-2">
                        <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
                          COA period
                        </span>
                        <div data-testid="exp-coa-period"
                          className="block min-h-10 w-full break-words rounded-md border border-[#E9E9EB] bg-[#F7F8FA] px-3 py-2 text-sm leading-5 text-[#374151]">
                          {periodLabel(schoolChoices?.period) || "—"}
                          <span className="ml-2 whitespace-nowrap text-[11px] text-[#9aa1a9]">set by the date paid</span>
                        </div>
                      </div>
                    ) : <div />}
                  </>
                );
              })()}
              {/* What applies to the third answer, and only when it does
                  (ZEN-833). The nudge, the reserve box, the full warning and
                  the "No AOTC option" note are gone by her design. */}
              {isComputerCat && (reserveOffered || drawAnswer === "reserve") && (
                <label className="sm:col-span-2 flex items-start gap-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-3 py-2" data-testid="exp-aotc-computer-gate">
                  {/* Unticking changes no answer: it only stops the save until the
                      box is ticked or another answer is chosen, as the scholarship
                      box does. Clearing the reservation here hid it, and on a
                      settled expense rewrote its frozen 529 answer (ZEN-826;
                      outside review, ZEN-833 round 1). */}
                  <input type="checkbox" checked={computerConfirmed} onChange={(e) => setComputerConfirmed(e.target.checked)}
                    data-testid="exp-aotc-computer-check" className="mt-0.5" />
                  <div>
                    <span className="text-xs text-[#374151]">Was this computer or technology needed for a course of study?</span>
                    {drawAnswer === "reserve" && !computerConfirmed && <span className="mt-0.5 block text-[10px] font-semibold text-[#D97706]" data-testid="exp-aotc-computer-required">Required to save this AOTC reservation.</span>}
                  </div>
                </label>
              )}
              {drawAnswer === "reserve" && (aotcData?.tax_free_aid || 0) > 0 && (
                <label className={`sm:col-span-2 flex items-start gap-2 rounded-md border px-2.5 py-1.5 cursor-pointer ${f.aotc_scholarship_confirmed ? "border-[#077A39]/20 bg-[#077A39]/5" : "border-[#D97706]/30 bg-[#D97706]/5"}`} data-testid="exp-aotc-scholarship-check">
                  <input type="checkbox" checked={!!f.aotc_scholarship_confirmed} onChange={(e) => set("aotc_scholarship_confirmed", e.target.checked)} data-testid="exp-aotc-scholarship-input" className="mt-0.5" />
                  <div>
                    <span className="text-[11px] text-[#374151]">{selectedStudent?.full_name || "This beneficiary"} has {money(aotcData.tax_free_aid, "USD")} in tax-free aid this year. Confirm this expense was not paid by that scholarship or grant.</span>
                    {!f.aotc_scholarship_confirmed && <span className="mt-0.5 block text-[10px] font-semibold text-[#D97706]">Required to save this AOTC reservation.</span>}
                  </div>
                </label>
              )}
              {/* Kept from before; not named by the design, so shown to her. */}
              {drawAnswer === "reserve" && !isEdit && recur.enabled && (
                <p className="sm:col-span-2 rounded-md border border-[#034AA2]/30 bg-[#034AA2]/10 px-3 py-2 text-xs font-semibold text-[#034AA2]" data-testid="exp-aotc-repeating-note">{AOTC_REPEATING_NOTE}</p>
              )}
              {drawAnswer === "reserve" && f.category === "Books" && (
                <p className="sm:col-span-2 text-[11px] text-[#6b7280]" data-testid="exp-aotc-books-note">Course materials qualify for AOTC only if needed for the coursework.</p>
              )}
              {drawAnswer === "reserve" && f.draw_from_529 === false && f.reimbursed_by_ref && f.reimbursed_by_ref !== "none" && (
                <p className="sm:col-span-2 text-[11px] text-[#6b7280]" data-testid="exp-aotc-reimb-note">A family reimbursement doesn&rsquo;t affect AOTC; only tax-free scholarships, grants, or 529 withdrawals do.</p>
              )}
            </div>
          </div>
          )}
          {!f.eligible_529 && (
            <ReimbursedByField locked={answersFrozen} f={f} set={set} parties={parties} addReimburserOpen={addReimburserOpen} setAddReimburserOpen={setAddReimburserOpen} newReimburserNick={newReimburserNick} setNewReimburserNick={setNewReimburserNick} qc={qc} />
          )}


          {/* Receipt attachments */}
          <div className="sm:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Documents{attachments.length > 1 ? ` (${attachments.length})` : ""}</span>
            {/* Existing receipts */}
            {attachments.length > 0 && (
              <div className="mb-1.5 space-y-1" data-testid="exp-receipt-existing">
                {attachments.map((a, i) => (
                  <div key={a.id} className="flex items-center gap-2">
                    {a.path ? (
                      <a href={assetUrl(a.path)} target="_blank" rel="noreferrer" data-testid={`exp-receipt-view-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-[#034AA2]/10 px-2.5 py-1 text-xs font-semibold text-[#034AA2] hover:bg-[#034AA2]/20">
                        <Paperclip size={14} weight="bold" /> {a.filename}
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-[#E9E9EB] px-2.5 py-1 text-xs font-semibold text-[#6b7280]"
                        title="This receipt has no attached file">
                        <Paperclip size={14} weight="bold" /> {a.filename}
                      </span>
                    )}
                    {/* Both kinds of document can come off again (owner ruling 2026-09-27). */}
                    <button type="button" onClick={() => askToRemove(a.id)}
                      data-testid={`exp-receipt-remove-${i}`}
                      className="rounded p-0.5 text-[#F01919] hover:bg-[#F01919]/10"
                      /* One name for one action (owner, 2026-09-28). */
                      title="Remove document">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                ))}
                <DeleteDialog
                  open={!!removingDocId}
                  onClose={() => { removal.cancel(); setRemovingDocId(null); setRemovingDoc(false); }}
                  onConfirm={removeDocument}
                  noun="document"
                  busy={removingDoc}
                  testid="exp-receipt-remove"
                  title="Remove this document from this expense?"
                  confirmLabel="Remove"
                  body={
                    <>
                      {/* Not the severe typed-word confirmation: most removals destroy nothing
                          (owner ruling 2026-10-02). Its words are not repeated here because a
                          test reads this source. The one case that does destroy a file gets one
                          accurate sentence, and the server decides when it shows. */}
                      {removingDeletesTheFile && (
                        <p className="text-sm text-[#374151]" data-testid="exp-receipt-remove-deletes-file">
                          This also deletes the file, because no other expense is using it.
                        </p>
                      )}
                      {removingIsTheLastOne && (
                        <p className="text-sm text-[#374151]" data-testid="exp-receipt-remove-last">
                          Expenses with no document show up as gaps in your tax packet.
                        </p>
                      )}
                      {removingIsShared && (
                        <p className="text-sm text-[#374151]" data-testid="exp-receipt-remove-shared">
                          Your other expense(s) using it keep it.
                        </p>
                      )}
                    </>
                  }
                />
              </div>
            )}
            {/* Newly selected files */}
            {receiptFiles.length > 0 && (
              <div className="mb-1.5 space-y-1" data-testid="exp-receipt-new-files">
                {receiptFiles.map((rf, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-[#22C55E]/10 px-2.5 py-1 text-xs font-semibold text-[#077A39]">
                      <Paperclip size={14} weight="bold" /> {rf.name}
                    </span>
                    <button type="button" onClick={() => setReceiptFiles((prev) => prev.filter((_, j) => j !== i))} className="rounded p-0.5 text-[#F01919] hover:bg-[#F01919]/10" title="Remove document"><Trash size={14} weight="bold" /></button>
                  </div>
                ))}
              </div>
            )}
            <label
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#034AA2]", "bg-[#034AA2]/5"); }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/5"); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/5");
                // A dropped HEIC often arrives with an empty or generic type,
                // because the browser does not always recognise it. Filtering
                // on type alone would silently swallow the exact file this
                // dialog now accepts, so the filename decides as well.
                const dropped = Array.from(e.dataTransfer.files || []).filter(
                  f => f.type.startsWith("image/") || f.type === "application/pdf" || hasAcceptedExtension(f.name));
                if (dropped.length) setReceiptFiles((prev) => [...prev, ...dropped]);
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-[#C2C9D1] bg-white px-3 py-2 text-sm text-[#6b7280] transition-colors hover:border-[#034AA2]" data-testid="exp-receipt-label">
              <Paperclip size={16} weight="bold" className="text-[#034AA2]" />
              Drop a file here or click to attach (PDF or image)
              <input type="file" accept={RECEIPT_ACCEPT} multiple className="hidden" onChange={(e) => { setReceiptFiles((prev) => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ""; }} data-testid="exp-receipt-input" />
            </label>
          </div>

          <div className="sm:col-span-2">
            <Inp label="Notes" value={f.notes} onChange={(v) => set("notes", v)} testid="exp-notes" />
          </div>
        </div>

        {/* 529 state disclaimer footer */}
        {f.eligible_529 && (
        <p className="mt-3 border-t border-[#C2C9D1] pt-2 text-xs leading-snug text-[#6b7280]" data-testid="exp-disclaimer-footer">{GROUP_DISCLAIMER_FOOTER}</p>
        )}

        {/* Button convention: equal-width Cancel (gray outline) + Save (green).
            The primary action is always "Save" — never "Add expense" or similar. */}
        <DialogFooter className="mt-2 gap-2">
          <Btn variant="ghost" className="flex-1" onClick={onClose} data-testid="exp-cancel">Cancel</Btn>
          <Btn enterPrimary variant="green" className="flex-1" onClick={save} disabled={busy} data-testid="exp-save">
            {busy ? "Saving…" : "Save"}
          </Btn>
        </DialogFooter>
      </DialogContent>

      {/* Custom category popup */}
      {/* SAME RULE, AND THIS ONE WAS FURTHER OUT OF REACH OF IT. (ZEN-100)
          This dialog is rendered at the top level of the component, so unlike
          the picker that opens it, it is not inside the "not 529-eligible"
          branch at all — switching the answer to Yes left it open, and
          confirming wrote free text onto a QUALIFIED expense. Free naming
          belongs to the family's own list; the eligible list is the IRS one. */}
      <Dialog open={customCatOpen && f.eligible_529 === false} onOpenChange={(o) => { if (!o) setCustomCatOpen(false); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Name this expense category</DialogTitle></DialogHeader>
          <input
            type="text"
            value={customCatName}
            onChange={(e) => setCustomCatName(e.target.value.slice(0, CUSTOM_CATEGORY_MAX_LENGTH))}
            data-testid="custom-cat-input"
            placeholder="e.g., subway pass, varsity soccer"
            maxLength={CUSTOM_CATEGORY_MAX_LENGTH}
            className="h-10 w-full rounded-md border border-[#C2C9D1] px-3 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30"
            autoFocus
          />
          <p className="text-[10px] text-[#9CA3AF]">{customCatName.length}/{CUSTOM_CATEGORY_MAX_LENGTH} characters</p>
          <DialogFooter className="gap-2">
            <Btn variant="ghost" className="flex-1" onClick={() => { setCustomCatOpen(false); setCustomCatName(""); }} data-testid="custom-cat-cancel">Cancel</Btn>
            <Btn enterPrimary variant="green" className="flex-1" onClick={() => { if (!isReservedCategoryName(customCatName, NON_ELIGIBLE_RESERVED_NAMES) && customCatName.trim() && f.eligible_529 === false) { set("category", customCatName.trim()); } setCustomCatOpen(false); setCustomCatName(""); }} data-testid="custom-cat-confirm" disabled={!customCatName.trim() || isReservedCategoryName(customCatName, NON_ELIGIBLE_RESERVED_NAMES)}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tax year cap confirmation */}
      <Dialog open={recurCapConfirm} onOpenChange={(o) => !o && setRecurCapConfirm(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>This schedule runs across tax years {recurYears.join(" and ")}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#374151]">
            All <strong>{recurPreview.length}</strong> expenses will be created, from{" "}
            <strong>{recurPreview[0]?.toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"})}</strong> to{" "}
            <strong>{recurPreview[recurPreview.length - 1]?.toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"})}</strong>.
          </p>
          <p className="text-sm text-[#374151]">
            Each one is filed under the tax year it&apos;s paid in, not the {ty} shown on the form — so your records stay correct in both years.
          </p>
          {/* Keeps a descriptive action label rather than "Save": the count is the
              whole point of the confirmation. Styling follows the convention. */}
          <DialogFooter className="mt-2 gap-2">
            <Btn variant="ghost" className="flex-1" onClick={() => setRecurCapConfirm(false)} data-testid="recur-cap-cancel">Cancel</Btn>
            <Btn variant="green" className="flex-1" onClick={() => { setRecurCapConfirm(false); recurCapRef.current = true; setTimeout(save, 50); }} data-testid="recur-cap-confirm">Create {recurPreview.length} expenses</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function ReimbursedByField({ locked, f, set, parties, addReimburserOpen, setAddReimburserOpen, newReimburserNick, setNewReimburserNick, qc }) {
  return (
    <div>
      <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
        Reimbursed by a party?
        <InfoTip testid="exp-reimburse-info" text="Select &lsquo;Not reimbursed&rsquo; if no repayment is expected (e.g., the beneficiary paid and isn&rsquo;t owed back, or the owner paid without expecting repayment). Choose a party if they&rsquo;re expected to pay back whoever paid (from &lsquo;Paid by&rsquo; above). That creates a Private Settlement card in the Overview, for both the owner and the beneficiary; once it&rsquo;s settled, the expense is marked Closed." />
      </span>
      <select value={f.reimbursed_by_ref} onChange={(v2) => { const v = v2.target ? v2.target.value : v2; if (v === "__add_reimburser__") { setAddReimburserOpen(true); } else { set("reimbursed_by_ref", v); } }} data-testid="exp-reimbursed-by" disabled={locked}
        className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 disabled:bg-[#E9E9EB]">
        <option value="none">Not reimbursed</option>
        <option value="owner">{parties?.owner?.nickname || "Owner"}</option>
        {(parties?.custom || []).map((p) => <option key={p.id} value={`party:${p.id}`}>{p.nickname}</option>)}
        <option value="__add_reimburser__">＋ Add party…</option>
      </select>
      {addReimburserOpen && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <input value={newReimburserNick} onChange={(e) => setNewReimburserNick(e.target.value)} placeholder="Nickname (e.g., Grandpa)" autoFocus data-testid="exp-add-reimburser-input"
            className="h-9 flex-1 rounded-md border border-[#C2C9D1] px-2 text-sm" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); document.querySelector('[data-testid="exp-add-reimburser-ok"]')?.click(); } }} />
          <button type="button" data-testid="exp-add-reimburser-ok" disabled={!newReimburserNick.trim()}
            className="h-9 rounded-md bg-[#034AA2] px-3 text-xs font-semibold text-white hover:bg-[#023d8a] disabled:opacity-50"
            onClick={async () => { try { const { data: np } = await api.post("/parties", { full_name: newReimburserNick.trim(), nickname: newReimburserNick.trim(), role: "other" }); qc.invalidateQueries({ queryKey: ["parties"] }); set("reimbursed_by_ref", `party:${np.id}`); } catch (e) { toast.error(apiErr(e)); } setAddReimburserOpen(false); setNewReimburserNick(""); }}>
            Add</button>
          <button type="button" onClick={() => { setAddReimburserOpen(false); setNewReimburserNick(""); set("reimbursed_by_ref", "none"); }} className="h-9 rounded-md border border-[#C2C9D1] px-3 text-xs font-semibold text-[#6b7280] hover:bg-[#E9E9EB]">Cancel</button>
        </div>
      )}
    </div>
  );
}


// AN ACCOUNT ADDED HERE BELONGS TO WHOEVER PAID. (Adversarial review, round 1.)
//
// This form used to post neither the holder nor the beneficiary, so the server
// filed every account it created under the ACCOUNT OWNER. Add one while
// recording an expense the beneficiary paid and you got an account of yours —
// which then vanished from the beneficiary's list, and, if you ticked the
// default box, replaced YOUR default instead of theirs.
function PsInlineAdd({ types, onCancel, onSaved, ownerName, holderType = "owner", holderStudentId = null, holderName = "you" }) {
  const [p, setP] = useState({ type: "credit_card", last4: "", institution: "", default_currency: "", is_default: false, note: "", oneTime: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const autoLabel = psLabel({ institution: p.institution, last4: p.last4, currency: curCode(p.default_currency, "USD"), type: p.type }, ownerName);

  const save = async () => {
    setBusy(true);
    try {
      // Same completeness rule as the full account dialog — this posts to the
      // same endpoint, which now requires both. Without these checks the owner
      // would get a raw 422 from the API instead of a usable message.
      const last4 = (p.last4 || "").replace(/\D/g, "").slice(0, 4);
      if (!(p.institution || "").trim()) { toast.error("Institution is required — it's what tells your accounts apart"); setBusy(false); return; }
      if (last4.length !== 4) { toast.error("Enter 4 digits — use 1111 if you'd rather not record the real ones"); setBusy(false); return; }
      const body = { nickname: autoLabel || "Account", type: p.type, last4,
        institution: p.institution, default_currency: curCode(p.default_currency),
        owner_type: holderType, student_id: holderStudentId,
        is_default: p.oneTime ? false : p.is_default, note: p.note };
      const { data } = await api.post("/accounts-cards", body);
      if (p.oneTime) await api.patch(`/accounts-cards/${data.id}/archive`, { archived: true });
      toast.success(p.oneTime ? "Added for this expense" : "Payment account saved");
      onSaved(data.id, p.default_currency);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-enter-scope className="rounded-lg border border-dashed border-[#034AA2]/40 bg-[#034AA2]/5 p-3" data-testid="exp-ps-add-form">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-[#034AA2]">New payment account</span>
        <button type="button" onClick={onCancel} className="text-[#6b7280] hover:text-[#1f2937]" aria-label="cancel"><X size={16} weight="bold" /></button>
      </div>
      {/* Auto-generated label preview */}
      <div className="mb-2" data-testid="ps-nickname">
        <span className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-[#6b7280]">Account label <span className="rounded bg-[#E9E9EB] px-1 py-px text-[9px] font-bold uppercase tracking-wider text-[#6b7280]">auto</span></span>
        <div className="flex h-9 items-center rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-2 text-[13px] font-semibold text-[#1f2937]">
          {autoLabel}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Mini label="Type"><select value={p.type} onChange={(e) => set("type", e.target.value)} data-testid="ps-type" className="ps-inp">{types.map((t) => <option key={t} value={t}>{nameOf(PS_TYPE_META, t, {}).label || UNKNOWN_ACCOUNT_TYPE}</option>)}</select></Mini>
        <Mini label="Institution · short code"><input value={p.institution} onChange={(e) => set("institution", e.target.value)} data-testid="ps-institution" placeholder="WF, BoA, RBC, TD, Chase" className="ps-inp" /></Mini>
        <Mini label="Last 4 digits"><input value={p.last4} onChange={(e) => set("last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="ps-last4" placeholder="0000" inputMode="numeric" className="ps-inp" /></Mini>
        <Mini label="Currency"><CurrencySelect value={p.default_currency} onChange={(v) => set("default_currency", v)} testid="ps-currency" allowEmpty /></Mini>
        {p.type === "other" && <Mini label="Describe (note)"><input value={p.note} onChange={(e) => set("note", e.target.value)} data-testid="ps-note" placeholder="e.g., Venmo" className="ps-inp" /></Mini>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        <label className="flex items-center gap-1.5 font-medium"><input type="checkbox" checked={p.is_default} disabled={p.oneTime} onChange={(e) => set("is_default", e.target.checked)} data-testid="ps-default" /> {holderType === "student" ? `Make this ${holderName}'s default` : "Make this your default"}</label>
        <label className="flex items-center gap-1.5 font-medium"><input type="checkbox" checked={p.oneTime} onChange={(e) => set("oneTime", e.target.checked)} data-testid="ps-onetime" /> Use just for this expense</label>
      </div>
      <div className="mt-2 flex justify-end">
        <Btn enterPrimary className="h-8" onClick={save} disabled={busy} data-testid="ps-save"><Plus size={14} weight="bold" /> {busy ? "Saving…" : "Save"}</Btn>
      </div>
      <style>{`.ps-inp{height:36px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:13px;background:#fff}`}</style>
    </div>
  );
}
function Mini({ label, children }) { return <label className="block"><span className="mb-0.5 block text-[11px] font-semibold text-[#6b7280]">{label}</span>{children}</label>; }

function Inp({ label, value, onChange, type = "text", testid, placeholder, step }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">{label}</span>
      <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-3 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30" />
    </label>
  );
}
function Sel({ label, value, onChange, children, testid, disabled, className = "" }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled}
        className={`h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 disabled:bg-[#E9E9EB] ${className}`}>
        {children}
      </select>
    </label>
  );
}


